import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
  type WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faker } from '@faker-js/faker/locale/pt_BR';
import { catchError, forkJoin, map, of, switchMap, timer, type Observable, type Subscription } from 'rxjs';
import type { Agent } from '../../../core/api/generated/models/Agent';
import type { AgentArchetype } from '../../../core/api/generated/models/AgentArchetype';
import type { AgentBuilderContextRequest } from '../../../core/api/generated/models/AgentBuilderContextRequest';
import type { AgentBuilderReferenceCatalog } from '../../../core/api/generated/models/AgentBuilderReferenceCatalog';
import type { AgentBuilderRunEvent } from '../../../core/api/generated/models/AgentBuilderRunEvent';
import type { AgentBuilderTriggerSuggestion } from '../../../core/api/generated/models/AgentBuilderTriggerSuggestion';
import type { AgentChannelConfigChannelTypeEnumKey } from '../../../core/api/generated/models/AgentChannelConfig';
import type { AgentDeployment } from '../../../core/api/generated/models/AgentDeployment';
import type { AgentDeploymentRequest } from '../../../core/api/generated/models/AgentDeploymentRequest';
import type { AgentGraphDefinition } from '../../../core/api/generated/models/AgentGraphDefinition';
import type { AgentMissionBlock } from '../../../core/api/generated/models/AgentMissionBlock';
import type { AgentMissionDiagnostic } from '../../../core/api/generated/models/AgentMissionDiagnostic';
import type { AgentMissionDocument } from '../../../core/api/generated/models/AgentMissionDocument';
import type { AgentMissionReference } from '../../../core/api/generated/models/AgentMissionReference';
import type { AgentRun } from '../../../core/api/generated/models/AgentRun';
import type { AgentSkill } from '../../../core/api/generated/models/AgentSkill';
import type { AgentSkillVersion } from '../../../core/api/generated/models/AgentSkillVersion';
import type { AgentToolDefinition } from '../../../core/api/generated/models/AgentToolDefinition';
import type { AgentTriggerPolicy } from '../../../core/api/generated/models/AgentTriggerPolicy';
import type { AgentUpsertRequest } from '../../../core/api/generated/models/AgentUpsertRequest';
import type { AgentVersion } from '../../../core/api/generated/models/AgentVersion';
import type { AgentVersionDraftRequest } from '../../../core/api/generated/models/AgentVersionDraftRequest';
import type { AgentVersionSkillInput } from '../../../core/api/generated/models/AgentVersionSkillInput';
import type { AgentVersionToolInput } from '../../../core/api/generated/models/AgentVersionToolInput';
import type { Automation } from '../../../core/api/generated/models/Automation';
import type { BusinessScopedSoftRef } from '../../../core/api/generated/models/BusinessScopedSoftRef';
import type { ChannelAccount } from '../../../core/api/generated/models/ChannelAccount';
import type { CrmSegment } from '../../../core/api/generated/models/CrmSegment';
import type { Integration } from '../../../core/api/generated/models/Integration';
import type { IntegrationsListDataResponse } from '../../../core/api/generated/models/IntegrationsListDataResponse';
import type { KnowledgeBase } from '../../../core/api/generated/models/KnowledgeBase';
import type { KnowledgeBaseListResponse } from '../../../core/api/generated/models/KnowledgeBaseListResponse';
import type { ListAutomationsResponse } from '../../../core/api/generated/models/ListAutomationsResponse';
import { AuthStore } from '../../../core/auth/auth.store';
import { ToastrService } from '../../../shared/ui';
import { findProviderEntry } from '../../integracoes/catalog/provider-catalog';
import { CrmApiService } from '../../crm/crm-api.service';
import { SettingsCoreApiService } from '../../settings/settings-core-api.service';
import {
  type AgentChannelConfigInput,
  type AgentGoalInput,
  AgentsApiService,
  type AgentTriggerPolicyAssignmentRecord,
  type AgentTriggerPolicyInput,
} from '../agents-api.service';
import { AgentMissionRealtimeService } from './agent-mission-realtime.service';
import type { MissionEditorSnapshot } from './agent-mission-quill';
import {
  type BuilderToolFieldKind,
  type BuilderToolFieldOption,
  type BuilderToolFieldValue,
  type BuilderToolFieldView,
  type BuilderMissionMention,
  type BuilderMissionMentionGroup,
  type BuilderToolMention,
  type BuilderToolPolicyDraft,
  type BuilderAllowedChannelOption,
  type AgentBuilderDeploymentMode,
  type AgentBuilderDeploymentStatus,
  type AgentBuilderIntensity,
  type AgentBuilderMode,
  type AgentBuilderStep,
  type AgentBuilderStepId,
  type AgentBuilderTone,
  type AgentBuilderVersionResolution,
  type BuilderCollectionField,
  type BuilderTriggerDraft,
  type ChannelBehaviorDraft,
  type MissionRunStatus,
} from './agent-builder.types';
import {
  bindDescriptorsFromTools,
  buildToolFieldViews,
  coerceFieldValue,
  compactToolPolicyDraft,
  emptyToolPolicyDraft,
  normalizeToolPolicyDraft,
  optionalToolFields,
  optionsForBindDescriptor,
  type BindDescriptor,
  type ToolFieldCatalogs,
} from './agent-tool-schema';

const BUILDER_STEPS: readonly AgentBuilderStep[] = [
  { id: 'context', label: 'Contexto', summary: 'Objetivo e canais' },
  { id: 'persona', label: 'Persona', summary: 'Identidade e resposta' },
  { id: 'libraries', label: 'Bibliotecas', summary: 'Fontes de conhecimento' },
  { id: 'tools', label: 'Ferramentas', summary: 'Conexões e políticas' },
  { id: 'mission', label: 'Missão', summary: 'Missão estruturada' },
];

const TONE_OPTIONS: readonly { value: AgentBuilderTone; label: string; policy: string }[] = [
  {
    value: 'consultivo',
    label: 'Consultivo',
    policy: 'consultivo, claro e orientado a próximos passos',
  },
  { value: 'direto', label: 'Direto', policy: 'direto, objetivo e sem rodeios' },
  { value: 'acolhedor', label: 'Acolhedor', policy: 'acolhedor, paciente e cuidadoso' },
  {
    value: 'tecnico',
    label: 'Técnico',
    policy: 'técnico, preciso e adequado para operações complexas',
  },
];

const INTENSITY_OPTIONS: readonly {
  value: AgentBuilderIntensity;
  label: string;
  policy: string;
}[] = [
  {
    value: 'equilibrado',
    label: 'Equilibrado',
    policy: 'atua proativamente quando há evidência suficiente e faz handoff em exceções',
  },
  {
    value: 'conservador',
    label: 'Conservador',
    policy: 'prefere pedir confirmação e transferir quando houver risco ou ambiguidade',
  },
  {
    value: 'proativo',
    label: 'Proativo',
    policy: 'antecipa próximos passos e sugere ações quando as permissões permitem',
  },
];

const DEPLOYMENT_MODE_OPTIONS: readonly { value: AgentBuilderDeploymentMode; label: string }[] = [
  { value: 'automatic_reply', label: 'Resposta automática' },
  { value: 'live_assist', label: 'Assistente do humano' },
];

const DEPLOYMENT_STATUS_OPTIONS: readonly { value: AgentBuilderDeploymentStatus; label: string }[] =
  [
    { value: 'active', label: 'Ativo' },
    { value: 'paused', label: 'Pausado' },
    { value: 'disabled', label: 'Desativado' },
  ];

const VERSION_RESOLUTION_OPTIONS: readonly {
  value: AgentBuilderVersionResolution;
  label: string;
}[] = [
  { value: 'latest_published_version', label: 'Última publicada' },
  { value: 'current_agent_version', label: 'Versão atual do agente' },
  { value: 'fixed_agent_version', label: 'Fixar versão publicada' },
];

const RESPONSE_LENGTH_SCALE: readonly ChannelBehaviorDraft['messageLength'][] = [
  'curta',
  'normal',
  'detalhada',
];

const INTENSITY_SCALE: readonly AgentBuilderIntensity[] = [
  'conservador',
  'equilibrado',
  'proativo',
];

const KNOWLEDGE_TYPE_LABELS: Record<string, string> = {
  faq: 'FAQ',
  sales_scripts: 'Scripts comerciais',
  processes: 'Processos',
  policies: 'Políticas',
  playbooks: 'Playbooks',
  playbook: 'Playbook',
  policy: 'Política',
  product_catalog: 'Catálogo',
  process: 'Processo',
  troubleshooting: 'Suporte',
  sales_script: 'Script comercial',
  custom: 'Customizada',
};

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  instagram_dm: 'Instagram DM',
  email: 'E-mail',
  sms: 'SMS',
  webchat: 'Webchat',
  custom: 'Customizado',
};

const CHANNEL_BRAND_COLORS: Record<string, string> = {
  webchat: '#2563EB',
  custom: '#0F172A',
};

const EMPTY_AUTOMATIONS_RESPONSE: ListAutomationsResponse = {
  data: [],
  meta: {
    page: 1,
    pageSize: 100,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

const EMPTY_INTEGRATIONS_RESPONSE: IntegrationsListDataResponse = {
  data: [],
};

const EMPTY_KNOWLEDGE_BASES_RESPONSE: KnowledgeBaseListResponse = {
  items: [],
  pagination: {
    page: 1,
    pageSize: 100,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

const ALLOWED_CHANNEL_OPTIONS: readonly BuilderAllowedChannelOption[] = [
  allowedChannelOption('whatsapp', 'WhatsApp', 'meta-whatsapp'),
  allowedChannelOption('instagram_dm', 'Instagram DM', 'meta-instagram'),
  allowedChannelOption('webchat', 'Webchat', 'webchat'),
  allowedChannelOption('email', 'E-mail', 'gmail'),
  allowedChannelOption('sms', 'SMS', 'twilio'),
  allowedChannelOption('custom', 'Customizado', 'custom'),
];

const FIELD_SOURCE_PREFIX = 'conversation';
const RUNTIME_MODEL_NODE = 'call_model';
const BUILDER_GENERATION_MODEL_NODE = 'call_model';
const FALLBACK_ARCHETYPE_MODEL = 'openai/gpt-4.1-mini';

const SYSTEM_AUTOMATION_MENTIONS: readonly BuilderMissionMention[] = [
  {
    type: 'automation',
    raw: '>automation:system.block_contact',
    targetId: 'system.block_contact',
    label: 'Bloquear contato',
    description: 'Impede novas interações automáticas com o contato atual.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Bloquear contato',
  },
  {
    type: 'automation',
    raw: '>automation:system.close_conversation',
    targetId: 'system.close_conversation',
    label: 'Encerrar conversa',
    description: 'Finaliza a conversa quando a etapa estiver resolvida ou fora de escopo.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Encerrar conversa',
  },
  {
    type: 'automation',
    raw: '>automation:system.collect_csat',
    targetId: 'system.collect_csat',
    label: 'Coletar pesquisa de satisfação',
    description: 'Solicita avaliação de satisfação antes de fechar o atendimento.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Coletar CSAT',
  },
  {
    type: 'automation',
    raw: '>automation:system.transfer.agent',
    targetId: 'system.transfer.agent',
    label: 'Transferir para agente',
    description: 'Transfere a execução para outro agente conforme a instrução da missão.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Transferir agente',
  },
  {
    type: 'automation',
    raw: '>automation:system.transfer.attendant',
    targetId: 'system.transfer.attendant',
    label: 'Transferir para atendente',
    description: 'Transfere para atendimento humano com o resumo da conversa.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Transferir atendente',
  },
  {
    type: 'automation',
    raw: '>automation:system.transfer.team',
    targetId: 'system.transfer.team',
    label: 'Transferir para equipe',
    description: 'Direciona a conversa para a equipe responsável conforme regras da missão.',
    groupLabel: 'Automação de sistema',
    tokenLabel: '>Transferir equipe',
  },
];

function allowedChannelOption(
  type: AgentChannelConfigChannelTypeEnumKey,
  label: string,
  integrationKey: string,
): BuilderAllowedChannelOption {
  const provider = findProviderEntry(integrationKey);
  const logoKey = provider?.id ?? integrationKey;
  return {
    type,
    label,
    integrationName: provider?.name ?? label,
    brandColor: provider?.brandColor ?? CHANNEL_BRAND_COLORS[integrationKey] ?? '#6B7280',
    logoColorPath: provider?.logoColorPath ?? `/integrations/${logoKey}.svg`,
    logoMonoPath: provider?.logoMonoPath ?? `/integrations/${logoKey}-mono.svg`,
  };
}

let localId = 0;

@Injectable({ providedIn: 'root' })
export class AgentBuilderStore {
  private readonly api = inject(AgentsApiService);
  private readonly crmApi = inject(CrmApiService);
  private readonly settingsApi = inject(SettingsCoreApiService);
  private readonly realtime = inject(AgentMissionRealtimeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);
  private readonly authStore = inject(AuthStore);
  private missionRunEventsSubscription: Subscription | null = null;
  private missionRunPollSubscription: Subscription | null = null;
  private readonly missionRunPollMaxAttempts = 48;

  readonly steps = BUILDER_STEPS;
  readonly toneOptions = TONE_OPTIONS;
  readonly intensityOptions = INTENSITY_OPTIONS;
  readonly deploymentModeOptions = DEPLOYMENT_MODE_OPTIONS;
  readonly deploymentStatusOptions = DEPLOYMENT_STATUS_OPTIONS;
  readonly versionResolutionOptions = VERSION_RESOLUTION_OPTIONS;
  readonly allowedChannelOptions = ALLOWED_CHANNEL_OPTIONS;

  readonly mode = signal<AgentBuilderMode>('create');
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly publishing = signal(false);
  readonly dirty = signal(false);
  readonly agentDirty = signal(false);
  readonly versionDirty = signal(false);
  readonly deploymentDirty = signal(false);
  readonly triggerDirty = signal(false);
  readonly savingExit = signal(false);
  readonly activeStep = signal<AgentBuilderStepId>('context');
  readonly furthestStepIndex = signal(0);

  readonly agents = signal<Agent[]>([]);
  readonly archetypes = signal<AgentArchetype[]>([]);
  readonly graphs = signal<AgentGraphDefinition[]>([]);
  readonly channelAccounts = signal<ChannelAccount[]>([]);
  readonly knowledgeBases = signal<KnowledgeBase[]>([]);
  readonly integrations = signal<Integration[]>([]);
  readonly businessHoursCalendars = signal<{ id: string; name: string }[]>([]);
  readonly automations = signal<Automation[]>([]);
  readonly tools = signal<AgentToolDefinition[]>([]);
  readonly skills = signal<AgentSkill[]>([]);
  readonly skillVersions = signal<Record<string, AgentSkillVersion>>({});
  readonly versions = signal<AgentVersion[]>([]);
  readonly deployments = signal<AgentDeployment[]>([]);
  readonly contactSegments = signal<CrmSegment[]>([]);
  readonly hasExecutionHistory = signal(false);

  readonly agentId = signal<string | null>(null);
  readonly selectedArchetypeId = signal<string | null>(null);
  readonly selectedChannelTypes = signal<AgentChannelConfigChannelTypeEnumKey[]>([]);
  readonly selectedChannelAccountIds = signal<string[]>([]);
  readonly deploymentSegmentByAccountId = signal<Record<string, string | null>>({});
  readonly deploymentModeByAccountId = signal<Record<string, AgentBuilderDeploymentMode>>({});
  readonly deploymentStatusByAccountId = signal<Record<string, AgentBuilderDeploymentStatus>>({});
  readonly deploymentVersionResolutionByAccountId = signal<
    Record<string, AgentBuilderVersionResolution>
  >({});
  readonly deploymentAgentVersionIdByAccountId = signal<Record<string, string | null>>({});
  readonly selectedKnowledgeBaseIds = signal<string[]>([]);
  readonly selectedIntegrationIds = signal<string[]>([]);
  readonly selectedToolIds = signal<string[]>([]);
  readonly selectedSkillIds = signal<string[]>([]);
  /**
   * Whether the user (directly or via an assisted-prompt suggestion) touched each binding list in
   * THIS session. Only touched lists are sent explicitly in the version draft payload; untouched
   * ones are omitted so the backend falls back to `sourceVersionId`'s existing bindings instead of
   * silently wiping them with an empty array (see agent.service.ts buildKnowledgeBaseInputs).
   */
  readonly knowledgeBasesTouched = signal(false);
  readonly toolsTouched = signal(false);
  readonly skillsTouched = signal(false);
  /** Id of the version `applyVersion()` hydrated from — sent as `sourceVersionId` so the backend
   * can inherit bindings/policies when creating a new version (e.g. the published version already
   * has active deployments and can no longer be patched in place). */
  readonly sourceVersionId = signal<string | null>(null);
  readonly toolPolicies = signal<Record<string, BuilderToolPolicyDraft>>({});
  /** Agent-level sub-account bindings, keyed by `${optionsFrom}:${kind}` (e.g. the pinned agenda). */
  readonly subAccountBindings = signal<Record<string, string>>({});
  /** Agent-level event template (default title + duration for calendar events). */
  readonly eventTemplate = signal<{ titleDefault: string; durationMinutes: number | null }>({
    titleDefault: '',
    durationMinutes: null,
  });
  /** Visibilidade da agenda: 'internal' vê todos os eventos; 'client' só os do contato (default seguro). */
  readonly agendaVisibility = signal<'internal' | 'client'>('client');

  readonly agentName = signal('');
  readonly presentationName = signal('');
  readonly intent = signal('');
  readonly tone = signal<AgentBuilderTone>('consultivo');
  readonly intensity = signal<AgentBuilderIntensity>('equilibrado');
  readonly librarySearch = signal('');
  readonly libraryTypeFilter = signal<string>('all');
  readonly integrationSearch = signal('');
  readonly toolSearch = signal('');
  readonly assistantPrompt = signal('');
  readonly deploymentMode = signal<AgentBuilderDeploymentMode>('automatic_reply');
  readonly deploymentStatus = signal<AgentBuilderDeploymentStatus>('active');
  readonly versionResolution = signal<AgentBuilderVersionResolution>('latest_published_version');

  readonly missionRunId = signal<string | null>(null);
  readonly missionRunStatus = signal<MissionRunStatus>('idle');
  readonly missionSourceDsl = signal('');
  readonly missionDocument = signal<AgentMissionDocument | null>(null);
  readonly editableMissionDocument = computed(
    () => this.missionDocument() ?? this.emptyMissionDocument(),
  );
  readonly missionDiagnostics = signal<AgentMissionDiagnostic[]>([]);
  readonly compiledPrompt = signal('');
  readonly versionDraftPatch = signal<Record<string, unknown>>({});
  readonly loadWarnings = signal<string[]>([]);
  readonly streamWarnings = signal<string[]>([]);
  readonly streamErrors = signal<string[]>([]);
  readonly validatingMission = signal(false);
  readonly triggerSuggestions = signal<AgentBuilderTriggerSuggestion[]>([]);
  readonly triggerDrafts = signal<BuilderTriggerDraft[]>([]);

  readonly channelBehaviors = signal<
    Record<AgentChannelConfigChannelTypeEnumKey, ChannelBehaviorDraft>
  >({
    whatsapp: this.defaultChannelBehavior('whatsapp'),
    instagram_dm: this.defaultChannelBehavior('instagram_dm'),
    webchat: this.defaultChannelBehavior('webchat'),
    email: this.defaultChannelBehavior('email'),
    sms: this.defaultChannelBehavior('sms'),
    custom: this.defaultChannelBehavior('custom'),
  });

  readonly collectionFields = signal<BuilderCollectionField[]>(defaultCollectionFields());

  readonly activeStepIndex = computed(() =>
    this.steps.findIndex((step) => step.id === this.activeStep()),
  );
  readonly selectedAgent = computed(
    () => this.agents().find((agent) => agent.id === this.agentId()) ?? null,
  );
  readonly currentVersion = computed(() => {
    const currentVersionId = this.selectedAgent()?.currentVersionId ?? null;
    return currentVersionId
      ? this.versions().find((version) => version.id === currentVersionId) ?? null
      : null;
  });
  readonly publishedVersions = computed(() =>
    this.versions().filter((version) => version.status === 'published'),
  );
  readonly selectedArchetype = computed(
    () =>
      this.archetypes().find((archetype) => archetype.id === this.selectedArchetypeId()) ?? null,
  );
  readonly selectedChannelAccounts = computed(() =>
    this.channelAccounts().filter((account) =>
      this.selectedChannelAccountIds().includes(account.id),
    ),
  );
  readonly selectedAllowedChannels = computed(() => {
    const selected = new Set(this.selectedChannelTypes());
    return this.allowedChannelOptions.filter((channel) => selected.has(channel.type));
  });
  readonly selectedKnowledgeBases = computed(() =>
    this.knowledgeBases().filter((base) => this.selectedKnowledgeBaseIds().includes(base.id)),
  );
  readonly selectedIntegrations = computed(() =>
    this.integrations().filter((integration) =>
      this.selectedIntegrationIds().includes(integration.id),
    ),
  );
  readonly selectedTools = computed(() =>
    this.tools().filter((tool) => this.selectedToolIds().includes(tool.id)),
  );
  readonly selectedSkills = computed(() =>
    this.skills().filter((skill) => this.selectedSkillIds().includes(skill.id)),
  );
  readonly selectedToolMentions = computed<BuilderToolMention[]>(() =>
    this.selectedTools().map((tool) => ({
      id: tool.id,
      key: tool.key,
      label: tool.displayName || tool.key,
      description: tool.description,
      riskLevel: tool.riskLevel,
    })),
  );
  readonly missionMentionOptions = computed<BuilderMissionMention[]>(() => {
    const libraries = this.selectedKnowledgeBases().map((base) => ({
      type: 'library' as const,
      raw: `@library:${base.id}`,
      targetId: base.id,
      label: base.name,
      description: `${this.knowledgeTypeLabel(base.type)} · ${base.activeDocumentCount || 0} documentos ativos`,
      groupLabel: 'Biblioteca',
      tokenLabel: `@${base.name}`,
    }));
    const tools = this.selectedTools().map((tool) => ({
      type: 'tool' as const,
      raw: `!tool:${tool.id}`,
      targetId: tool.id,
      label: tool.displayName || tool.key,
      description: this.toolRiskCopy(tool.riskLevel),
      groupLabel: 'Tool',
      tokenLabel: `!${tool.displayName || tool.key}`,
    }));
    const skills = this.selectedSkills().flatMap((skill) => {
      const version = this.skillVersions()[skill.id];
      if (!version) return [];
      return [
        {
          type: 'skill' as const,
          raw: `@skill:${version.id}`,
          targetId: version.id,
          label: skill.name,
          description: skill.description || skill.category || 'Skill ativa',
          groupLabel: 'Skill',
          tokenLabel: `@${skill.name}`,
        },
      ];
    });
    const automations = this.automations()
      .filter((automation) => automation.status === 'active' || automation.status === 'draft')
      .map((automation) => ({
        type: 'automation' as const,
        raw: `>automation:${automation.id}`,
        targetId: automation.id,
        label: automation.name,
        description: automation.status === 'active'
          ? 'Aciona a automação e transfere a execução do agente para o fluxo.'
          : 'Rascunho de automação disponível para desenho do fluxo.',
        groupLabel: 'Automação',
        tokenLabel: `>${automation.name}`,
      }));
    const fields = this.collectionFields().map((field) => ({
      type: 'field' as const,
      raw: `$field:${field.key}`,
      targetId: field.key,
      label: field.label,
      description: field.description || `${field.source}${field.required ? ' · obrigatório' : ''}`,
      groupLabel: 'Campo',
      tokenLabel: `$${field.label}`,
    }));
    return [
      ...libraries,
      ...tools,
      ...skills,
      ...SYSTEM_AUTOMATION_MENTIONS,
      ...automations,
      ...fields,
    ];
  });
  readonly missionMentionGroups = computed<BuilderMissionMentionGroup[]>(() => {
    const mentions = this.missionMentionOptions();
    return [
      {
        key: 'library',
        label: 'Bibliotecas',
        emptyLabel: 'Nenhuma biblioteca selecionada.',
        mentions: mentions.filter((mention) => mention.type === 'library'),
      },
      {
        key: 'tool',
        label: 'Tools',
        emptyLabel: 'Nenhuma tool habilitada.',
        mentions: mentions.filter((mention) => mention.type === 'tool'),
      },
      {
        key: 'skill',
        label: 'Skills',
        emptyLabel: 'Nenhuma skill ativa selecionada.',
        mentions: mentions.filter((mention) => mention.type === 'skill'),
      },
      {
        key: 'automation',
        label: 'Automações',
        emptyLabel: 'Nenhuma automação disponível.',
        mentions: mentions.filter((mention) => mention.type === 'automation'),
      },
      {
        key: 'field',
        label: 'Campos',
        emptyLabel: 'Nenhum campo sugerido.',
        mentions: mentions.filter((mention) => mention.type === 'field'),
      },
    ];
  });
  readonly selectedGraph = computed(() => {
    const archetype = this.selectedArchetype();
    if (archetype?.defaultGraphKey) {
      const archetypeGraph = this.graphs().find(
        (graph) =>
          graph.key === archetype.defaultGraphKey &&
          graph.version === archetype.defaultGraphVersion,
      );
      if (archetypeGraph) return archetypeGraph;
    }
    return (
      this.graphs().find((graph) => graph.key === 'conversational-agent') ??
      this.graphs().find((graph) => graph.key === 'conversation-agent') ??
      this.graphs()[0] ??
      null
    );
  });

  readonly visibleKnowledgeBases = computed(() => {
    const query = normalizeSearchText(this.librarySearch());
    const type = this.libraryTypeFilter();
    return this.knowledgeBases().filter((base) => {
      if (type !== 'all' && base.type !== type) return false;
      if (!query) return true;
      return normalizeSearchText(`${base.name} ${base.description ?? ''} ${base.type}`).includes(
        query,
      );
    });
  });

  readonly knowledgeTypeOptions = computed(() => {
    const types = [...new Set(this.knowledgeBases().map((base) => base.type))].sort();
    return [
      { value: 'all', label: 'Todos os tipos' },
      ...types.map((type) => ({ value: type, label: this.knowledgeTypeLabel(type) })),
    ];
  });

  readonly visibleIntegrations = computed(() => {
    const query = normalizeSearchText(this.integrationSearch());
    if (!query) return this.integrations();
    return this.integrations().filter((integration) =>
      normalizeSearchText(
        `${integration.displayName} ${integration.provider} ${integration.status}`,
      ).includes(query),
    );
  });

  readonly inferredTools = computed(() => {
    const integrationIds = new Set(this.selectedIntegrationIds());
    if (integrationIds.size === 0) return [];
    const providerKeys = new Set<string>(
      this.selectedIntegrations().map((integration) => integration.provider),
    );
    return this.tools().filter((tool) => {
      if (tool.toolFamily === 'system') return true;
      if (tool.integrationId) return integrationIds.has(tool.integrationId);
      return Boolean(tool.providerKey && providerKeys.has(tool.providerKey));
    });
  });

  readonly availableTools = computed(() => {
    const inferred = this.inferredTools();
    return inferred.length > 0 ? inferred : this.tools();
  });

  readonly visibleTools = computed(() => {
    const query = normalizeSearchText(this.toolSearch());
    if (!query) return this.availableTools();
    return this.availableTools().filter((tool) =>
      normalizeSearchText(
        `${tool.displayName} ${tool.key} ${tool.description} ${tool.owningDomain} ${tool.riskLevel}`,
      ).includes(query),
    );
  });

  readonly visibleSkills = computed(() => {
    const inferred = this.inferredSkills();
    return inferred.length > 0 ? inferred : this.skills();
  });

  readonly assistantSummary = computed(() => ({
    objective: this.selectedArchetype()?.name ?? 'Escolha um objetivo',
    intent: this.intent().trim() || this.assistantPrompt().trim() || 'Descreva o que o agente deve fazer',
    libraries: this.selectedKnowledgeBases().length,
    integrations: this.selectedIntegrations().length,
    capabilities: this.selectedTools().length + this.selectedSkills().length,
    tone: this.toneLabel(),
    intensity: this.intensityLabel(),
  }));

  readonly inferredSkills = computed(() => {
    const providerKeys = new Set<string>(
      this.selectedIntegrations().map((integration) => integration.provider),
    );
    if (providerKeys.size === 0) return [];
    return this.skills().filter(
      (skill) => !skill.integrationProviderKey || providerKeys.has(skill.integrationProviderKey),
    );
  });

  readonly stepSummaries = computed<Record<AgentBuilderStepId, string>>(() => ({
    context: this.selectedArchetype()?.name ?? 'Defina objetivo e canais',
    persona: `${this.presentationName().trim() || this.agentName().trim() || 'sem nome'} · ${this.toneLabel()}`,
    libraries: `${this.selectedKnowledgeBases().length} biblioteca(s)`,
    tools: `${this.selectedIntegrations().length} integração(ões) · ${this.selectedTools().length + this.selectedSkills().length} capacidade(s)`,
    mission: this.missionDocument()
      ? `${this.missionDocument()!.blocks.length} etapa(s)`
      : this.missionRunStatus() === 'idle'
        ? 'Editor pronto'
        : this.missionRunStatusLabel(),
    deployments: `${this.deployments().length} ativação(ões) existente(s)`,
  }));

  readonly canContinue = computed(() => {
    const step = this.activeStep();
    if (step === 'context') return Boolean(this.selectedArchetypeId()) && !this.saving();
    if (step === 'mission') return !this.saving();
    return !this.saving();
  });

  readonly publishBlockers = computed(() => {
    const blockers: string[] = [];
    if (!this.agentId()) blockers.push('Salve o rascunho do agente antes de publicar.');
    if (!this.missionDocument()) blockers.push('Valide a missão antes de publicar.');
    if (this.missionDiagnostics().some((diagnostic) => diagnostic.severity === 'error')) {
      blockers.push('Corrija os diagnósticos bloqueantes da missão.');
    }
    if (this.saving()) blockers.push('Aguarde o rascunho terminar de salvar.');
    if (this.publishing()) blockers.push('Publicação em andamento.');
    return blockers;
  });

  readonly canPublish = computed(() => this.publishBlockers().length === 0);

  reset(mode: AgentBuilderMode): void {
    this.stopMissionRunTracking();
    this.mode.set(mode);
    this.loading.set(false);
    this.saving.set(false);
    this.publishing.set(false);
    this.dirty.set(false);
    this.agentDirty.set(false);
    this.versionDirty.set(false);
    this.deploymentDirty.set(false);
    this.triggerDirty.set(false);
    this.savingExit.set(false);
    this.activeStep.set('context');
    this.furthestStepIndex.set(mode === 'edit' ? this.steps.length - 1 : 0);
    this.agentId.set(null);
    this.selectedArchetypeId.set(null);
    this.selectedChannelTypes.set([]);
    this.selectedChannelAccountIds.set([]);
    this.deploymentSegmentByAccountId.set({});
    this.deploymentModeByAccountId.set({});
    this.deploymentStatusByAccountId.set({});
    this.deploymentVersionResolutionByAccountId.set({});
    this.deploymentAgentVersionIdByAccountId.set({});
    this.selectedKnowledgeBaseIds.set([]);
    this.selectedIntegrationIds.set([]);
    this.selectedToolIds.set([]);
    this.selectedSkillIds.set([]);
    this.knowledgeBasesTouched.set(false);
    this.toolsTouched.set(false);
    this.skillsTouched.set(false);
    this.sourceVersionId.set(null);
    this.automations.set([]);
    this.toolPolicies.set({});
    this.subAccountBindings.set({});
    this.eventTemplate.set({ titleDefault: '', durationMinutes: null });
    this.agendaVisibility.set('client');
    this.agentName.set('');
    this.presentationName.set(this.suggestPresentationName());
    this.intent.set('');
    this.tone.set('consultivo');
    this.intensity.set('equilibrado');
    this.librarySearch.set('');
    this.libraryTypeFilter.set('all');
    this.integrationSearch.set('');
    this.toolSearch.set('');
    this.assistantPrompt.set('');
    this.deploymentMode.set('automatic_reply');
    this.deploymentStatus.set('active');
    this.versionResolution.set('latest_published_version');
    this.missionRunId.set(null);
    this.missionRunStatus.set('idle');
    this.missionSourceDsl.set('');
    this.missionDocument.set(null);
    this.missionDiagnostics.set([]);
    this.compiledPrompt.set('');
    this.versionDraftPatch.set({});
    this.loadWarnings.set([]);
    this.streamWarnings.set([]);
    this.streamErrors.set([]);
    this.triggerSuggestions.set([]);
    this.triggerDrafts.set([]);
    this.versions.set([]);
    this.deployments.set([]);
    this.contactSegments.set([]);
    this.hasExecutionHistory.set(false);
    this.collectionFields.set(defaultCollectionFields());
    this.channelBehaviors.set({
      whatsapp: this.defaultChannelBehavior('whatsapp'),
      instagram_dm: this.defaultChannelBehavior('instagram_dm'),
      webchat: this.defaultChannelBehavior('webchat'),
      email: this.defaultChannelBehavior('email'),
      sms: this.defaultChannelBehavior('sms'),
      custom: this.defaultChannelBehavior('custom'),
    });
  }

  load(mode: AgentBuilderMode, routeAgentId: string | null): void {
    this.reset(mode);
    this.loading.set(true);
    const canReadIntegrations = this.authStore.hasEveryPermission([
      'integrations:connections:read',
      'integrations:channel-accounts:read',
    ]);
    const businessId = this.authStore.business()?.id ?? null;

    forkJoin({
      agents: this.api.listAgents().pipe(this.withPartialLoadWarning('agentes')),
      archetypes: this.api.listArchetypes().pipe(this.withPartialLoadWarning('objetivos')),
      graphs: this.api.listGraphDefinitions().pipe(this.withPartialLoadWarning('fluxos')),
      tools: this.api.listTools().pipe(this.withPartialLoadWarning('tools')),
      skills: this.api.listSkills().pipe(this.withPartialLoadWarning('ações')),
      channelAccounts: canReadIntegrations ? this.api.listAllChannelAccounts() : of([]),
      integrations: canReadIntegrations
        ? this.api.listIntegrations()
        : of(EMPTY_INTEGRATIONS_RESPONSE),
      automations: this.api
        .listAutomations()
        .pipe(catchError(() => of(EMPTY_AUTOMATIONS_RESPONSE))),
      knowledgeBases: this.api
        .listKnowledgeBases()
        .pipe(this.withPartialLoadWarning('bibliotecas', EMPTY_KNOWLEDGE_BASES_RESPONSE)),
      businessHoursCalendars: businessId
        ? this.settingsApi
            .listBusinessHoursCalendars(businessId)
            .pipe(
              map((response) => response.calendars),
              catchError(() => of([])),
            )
        : of([]),
      contactSegments: this.crmApi
        .listSegments({ entityType: 'contact', isArchived: false, pageSize: 100 })
        .pipe(
          catchError(() => {
            this.loadWarnings.update((warnings) => [
              ...warnings,
              'Não foi possível carregar segmentos de contato.',
            ]);
            return of([] as CrmSegment[]);
          }),
        ),
    })
      .pipe(
        switchMap((result) =>
          this.api
            .resolveCurrentSkillVersions(result.skills.data)
            .pipe(
              catchError(() => {
                this.loadWarnings.update((warnings) => [
                  ...warnings,
                  'Não foi possível carregar versões de ações.',
                ]);
                return of({});
              }),
              map((skillVersions) => ({ ...result, skillVersions })),
            ),
        ),
        switchMap((result) => {
          const activeArchetypes = result.archetypes.data.filter(
            (archetype) => archetype.status?.toLowerCase() === 'active',
          );
          const availableArchetypes =
            activeArchetypes.length > 0
              ? activeArchetypes
              : result.archetypes.data.filter(
                  (archetype) => archetype.status?.toLowerCase() !== 'disabled',
                );
          this.agents.set(result.agents.data);
          this.archetypes.set(availableArchetypes);
          this.graphs.set(result.graphs.data);
          this.tools.set(result.tools.data.filter((tool) => tool.status !== 'disabled'));
          this.skills.set(result.skills.data);
          this.skillVersions.set(result.skillVersions);
          this.channelAccounts.set(
            result.channelAccounts.filter((account) => account.status === 'active'),
          );
          this.businessHoursCalendars.set(
            result.businessHoursCalendars.map((calendar) => ({ id: calendar.id, name: calendar.name })),
          );
          this.integrations.set(
            result.integrations.data.filter(
              (integration) =>
                integration.deletedAt === null || integration.deletedAt === undefined,
            ),
          );
          this.automations.set(
            result.automations.data.filter((automation) => automation.status !== 'archived'),
          );
          this.knowledgeBases.set(result.knowledgeBases.items);
          this.contactSegments.set(result.contactSegments.filter((segment) => !segment.isArchived));

          if (mode === 'edit' && routeAgentId) {
            const agent = result.agents.data.find((item) => item.id === routeAgentId) ?? null;
            if (!agent) {
              throw new Error('agent_not_found');
            }
            this.applyAgent(agent);
            return this.loadExistingAgentDetails(agent.id);
          }

          if (this.archetypes().length === 1) {
            this.selectedArchetypeId.set(this.archetypes()[0]!.id);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError((error: unknown) => {
          this.toast.error(
            error instanceof Error && error.message === 'agent_not_found'
              ? 'Agente não encontrado.'
              : 'Não foi possível carregar o construtor.',
          );
          return of(null);
        }),
      )
      .subscribe(() => {
        this.loading.set(false);
        this.dirty.set(false);
      });
  }

  refreshIntegrationsAndCapabilities(): void {
    if (
      !this.authStore.hasEveryPermission([
        'integrations:connections:read',
        'integrations:channel-accounts:read',
      ])
    ) {
      return;
    }

    forkJoin({
      integrations: this.api.listIntegrations(),
      channelAccounts: this.api.listAllChannelAccounts(),
      tools: this.api.listTools(),
      skills: this.api.listSkills(),
    })
      .pipe(
        switchMap((result) =>
          this.api
            .resolveCurrentSkillVersions(result.skills.data)
            .pipe(map((skillVersions) => ({ ...result, skillVersions }))),
        ),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((result) => {
        if (!result) return;
        this.integrations.set(result.integrations.data);
        this.channelAccounts.set(result.channelAccounts);
        this.tools.set(result.tools.data.filter((tool) => tool.status !== 'disabled'));
        this.skills.set(result.skills.data);
        this.skillVersions.set(result.skillVersions);
      });
  }

  goToStep(stepId: AgentBuilderStepId): void {
    const targetIndex = this.steps.findIndex((step) => step.id === stepId);
    if (targetIndex === -1 || targetIndex > this.furthestStepIndex()) return;
    this.activeStep.set(stepId);
  }

  goToEditStep(stepId: AgentBuilderStepId): void {
    const targetIndex = this.steps.findIndex((step) => step.id === stepId);
    if (targetIndex === -1) return;
    this.furthestStepIndex.update((current) => Math.max(current, targetIndex));
    this.activeStep.set(stepId);
  }

  next(): void {
    if (!this.canContinue()) return;
    const index = this.activeStepIndex();
    if (index === -1 || index >= this.steps.length - 1) return;

    if (this.activeStep() === 'context') {
      this.ensureAgentRoot()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.advanceTo(index + 1),
          error: () => this.toast.error('Não foi possível criar o rascunho do agente.'),
        });
      return;
    }

    if (
      this.activeStep() === 'persona' ||
      this.activeStep() === 'libraries' ||
      this.activeStep() === 'tools'
    ) {
      this.saveIncremental(false)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.advanceTo(index + 1),
          error: () => this.toast.error('Não foi possível salvar o rascunho do agente.'),
        });
      return;
    }

    this.advanceTo(index + 1);
  }

  previous(): void {
    const index = this.activeStepIndex();
    if (index > 0) {
      this.activeStep.set(this.steps[index - 1]!.id);
    }
  }

  selectArchetype(archetype: AgentArchetype): void {
    this.selectedArchetypeId.set(archetype.id);
    if (!this.agentName().trim()) {
      this.agentName.set(`Agente ${archetype.name}`);
    }
    if (!this.presentationName().trim()) {
      this.presentationName.set(this.suggestPresentationName());
    }
    if (!this.intent().trim()) {
      this.intent.set(
        readString(archetype.defaultGoalPolicy, 'northStar') ||
          archetype.description ||
          `Atender clientes como ${archetype.name}.`,
      );
    }
    this.markDirty();
  }

  toggleChannelAccount(id: string): void {
    const wasSelected = this.selectedChannelAccountIds().includes(id);
    this.toggleId(this.selectedChannelAccountIds, id);
    if (wasSelected) {
      this.deploymentSegmentByAccountId.update((segments) => {
        const next = { ...segments };
        delete next[id];
        return next;
      });
    } else {
      this.deploymentStatusByAccountId.update((statuses) => ({
        ...statuses,
        [id]: this.deploymentStatus(),
      }));
      this.deploymentModeByAccountId.update((modes) => ({
        ...modes,
        [id]: this.deploymentMode(),
      }));
      this.deploymentVersionResolutionByAccountId.update((versions) => ({
        ...versions,
        [id]: this.versionResolution(),
      }));
    }
    this.markDeploymentDirty();
  }

  toggleChannelType(type: AgentChannelConfigChannelTypeEnumKey): void {
    const willEnable = !this.selectedChannelTypes().includes(type);
    this.selectedChannelTypes.update((types) => {
      const selected = types.includes(type);
      return selected
        ? types.filter((item) => item !== type)
        : this.sortChannelTypes([...types, type]);
    });
    this.channelBehaviors.update((behaviors) => ({
      ...behaviors,
      [type]: {
        ...behaviors[type],
        enabled: willEnable,
      },
    }));
    this.markDirty();
  }

  setChannelReplyMode(
    type: AgentChannelConfigChannelTypeEnumKey,
    mode: AgentBuilderDeploymentMode,
  ): void {
    this.channelBehaviors.update((behaviors) => ({
      ...behaviors,
      [type]: {
        ...behaviors[type],
        replyMode: mode,
      },
    }));
    if (this.selectedChannelTypes().includes(type)) {
      this.deploymentMode.set(mode);
    }
    this.markDirty();
  }

  setChannelReplyModeFromToggle(type: AgentChannelConfigChannelTypeEnumKey, value: unknown): void {
    const mode = firstToggleValue(value);
    if (isDeploymentMode(mode)) {
      this.setChannelReplyMode(type, mode);
    }
  }

  setChannelMessageLength(
    type: AgentChannelConfigChannelTypeEnumKey,
    value: ChannelBehaviorDraft['messageLength'],
  ): void {
    this.channelBehaviors.update((behaviors) => ({
      ...behaviors,
      [type]: {
        ...behaviors[type],
        messageLength: value,
      },
    }));
    this.markDirty();
  }

  setChannelMessageLengthFromSelect(
    type: AgentChannelConfigChannelTypeEnumKey,
    value: unknown,
  ): void {
    if (isMessageLength(value)) {
      this.setChannelMessageLength(type, value);
    }
  }

  setResponseLength(value: ChannelBehaviorDraft['messageLength']): void {
    this.channelBehaviors.update((behaviors) => {
      const next = { ...behaviors };
      for (const channel of this.allowedChannelOptions) {
        next[channel.type] = {
          ...next[channel.type],
          messageLength: value,
        };
      }
      return next;
    });
    this.markDirty();
  }

  setResponseLengthFromSelect(value: unknown): void {
    if (isMessageLength(value)) {
      this.setResponseLength(value);
    }
  }

  setResponseLengthFromRange(event: Event): void {
    const index = this.rangeIndex(event, RESPONSE_LENGTH_SCALE.length);
    this.setResponseLength(RESPONSE_LENGTH_SCALE[index] ?? 'normal');
  }

  responseLengthScale(): number {
    return Math.max(0, RESPONSE_LENGTH_SCALE.indexOf(this.responseLength()));
  }

  setChannelBehaviorText(
    type: AgentChannelConfigChannelTypeEnumKey,
    key: 'toneStyle' | 'templatePolicy' | 'mediaPolicy' | 'asyncWindowPolicy' | 'fallbackPolicy',
    event: Event,
  ): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.channelBehaviors.update((behaviors) => ({
      ...behaviors,
      [type]: {
        ...behaviors[type],
        [key]: value,
      },
    }));
    this.markDirty();
  }

  toggleKnowledgeBase(id: string): void {
    this.toggleId(this.selectedKnowledgeBaseIds, id);
    this.knowledgeBasesTouched.set(true);
  }

  toggleIntegration(id: string): void {
    this.toggleId(this.selectedIntegrationIds, id);
  }

  toggleTool(id: string): void {
    this.toggleId(this.selectedToolIds, id);
    this.toolsTouched.set(true);
  }

  toolFieldViews(tool: AgentToolDefinition): BuilderToolFieldView[] {
    return buildToolFieldViews(tool, this.toolPolicies()[tool.id], this.toolFieldCatalogs());
  }

  availableOptionalToolFields(tool: AgentToolDefinition): BuilderToolFieldView[] {
    return optionalToolFields(this.toolFieldViews(tool));
  }

  addOptionalToolField(toolId: string, fieldKey: unknown): void {
    if (typeof fieldKey !== 'string' || !fieldKey) return;
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) => (field.key === fieldKey ? { ...field, enabled: true } : field)),
    );
  }

  hideOptionalToolField(toolId: string, fieldKey: string): void {
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) =>
        field.key === fieldKey && !field.required
          ? { ...field, enabled: false, value: emptyFieldValue(field.kind), guidance: '' }
          : field,
      ),
    );
  }

  setToolFieldDefault(
    toolId: string,
    fieldKey: string,
    kind: BuilderToolFieldKind,
    value: unknown,
  ): void {
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) =>
        field.key === fieldKey
          ? { ...field, enabled: true, value: coerceFieldValue(value, kind) }
          : field,
      ),
    );
  }

  setToolFieldDefaultFromEvent(
    toolId: string,
    fieldKey: string,
    kind: BuilderToolFieldKind,
    event: Event,
  ): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.setToolFieldDefault(toolId, fieldKey, kind, target.value);
  }

  setToolFieldGuidance(toolId: string, fieldKey: string, event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) =>
        field.key === fieldKey ? { ...field, enabled: true, guidance: value } : field,
      ),
    );
  }

  setToolFieldLocked(toolId: string, fieldKey: string, locked: boolean): void {
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) => (field.key === fieldKey ? { ...field, enabled: true, locked } : field)),
    );
  }

  toggleToolFieldMultiValue(
    toolId: string,
    fieldKey: string,
    value: string,
    checked: boolean,
  ): void {
    this.updateToolFields(toolId, (fields) =>
      fields.map((field) => {
        if (field.key !== fieldKey) return field;
        const current = Array.isArray(field.value) ? [...field.value] : [];
        const next = checked
          ? [...new Set([...current, value])]
          : current.filter((item) => item !== value);
        return { ...field, enabled: true, value: next };
      }),
    );
  }

  insertToolReference(toolId: string): void {
    const mention = this.missionMentionOptions().find(
      (option) => option.type === 'tool' && option.targetId === toolId,
    );
    if (mention) this.insertMissionReference(null, mention);
  }

  insertMissionReference(blockId: string | null, mention: BuilderMissionMention): void {
    const document = this.missionDocument() ?? this.emptyMissionDocument();
    const reference = {
      type: mention.type,
      raw: mention.raw,
      targetId: mention.targetId,
      label: mention.label,
      metadata: {
        source: 'agent_builder',
      },
    };
    const targetBlock = document.blocks.find((block) => block.id === blockId) ??
      document.blocks[document.blocks.length - 1] ?? {
        id: nextLocalId('block'),
        type: 'policy' as const,
        slug: 'referencias-da-missao',
        title: 'Referências da missão',
        body: '',
        fields: {},
        references: [],
        diagnostics: [],
      };
    const bodySuffix = targetBlock.body.includes(reference.raw) ? '' : ` ${reference.raw}`;
    const updatedBlock = {
      ...targetBlock,
      body: `${targetBlock.body}${bodySuffix}`.trimStart(),
      references: upsertReference(targetBlock.references, reference),
    };
    const existingBlock = document.blocks.some((block) => block.id === targetBlock.id);
    const blocks = existingBlock
      ? document.blocks.map((block) => (block.id === targetBlock.id ? updatedBlock : block))
      : [...document.blocks, updatedBlock];
    this.missionDocument.set({
      ...document,
      blocks,
      references: this.collectMissionReferences(blocks, document.references),
    });
    this.markDirty();
  }

  toggleSkill(skill: AgentSkill): void {
    if (!this.skillVersions()[skill.id]) return;
    this.toggleId(this.selectedSkillIds, skill.id);
    this.skillsTouched.set(true);
  }

  setAssistantPrompt(event: Event): void {
    this.assistantPrompt.set((event.target as HTMLTextAreaElement).value);
    this.markDirty();
  }

  applyAssistantPrompt(): void {
    const prompt = this.assistantPrompt().trim();
    if (!prompt) return;

    const inferredArchetype = this.bestArchetypeForPrompt(prompt);
    if (inferredArchetype && !this.selectedArchetypeId()) {
      this.selectArchetype(inferredArchetype);
    }

    this.intent.set(prompt);
    if (!this.agentName().trim()) {
      const objectiveName = this.selectedArchetype()?.name ?? inferredArchetype?.name ?? 'Assistido';
      this.agentName.set(`Agente ${objectiveName}`);
    }

    this.selectSuggestedKnowledgeBasesForPrompt(prompt);
    this.selectSuggestedIntegrationsForPrompt(prompt);
    this.markDirty();
  }

  startAssistedCreation(): void {
    this.applyAssistantPrompt();
    if (!this.selectedArchetypeId() && this.archetypes().length === 1) {
      this.selectArchetype(this.archetypes()[0]!);
    }

    const archetype = this.selectedArchetype();
    if (!this.intent().trim()) {
      this.intent.set(
        readString(archetype?.defaultGoalPolicy, 'northStar') ||
          archetype?.description ||
          'Atender clientes com clareza, consultar fontes permitidas e executar as ações habilitadas quando houver contexto suficiente.',
      );
    }
    if (!this.agentName().trim()) {
      this.agentName.set(archetype ? `Agente ${archetype.name}` : 'Novo agente');
    }

    this.goToEditStep('mission');
    this.startMissionGeneration();
  }

  setString(target: WritableSignal<string>, event: Event): void {
    target.set((event.target as HTMLInputElement | HTMLTextAreaElement).value);
    this.markDirty();
  }

  setTone(value: unknown): void {
    if (isTone(value)) {
      this.tone.set(value);
      this.markDirty();
    }
  }

  setIntensity(value: unknown): void {
    if (isIntensity(value)) {
      this.intensity.set(value);
      this.markDirty();
    }
  }

  setIntensityFromToggle(value: unknown): void {
    const intensity = firstToggleValue(value);
    if (isIntensity(intensity)) {
      this.setIntensity(intensity);
    }
  }

  setIntensityFromRange(event: Event): void {
    const index = this.rangeIndex(event, INTENSITY_SCALE.length);
    this.setIntensity(INTENSITY_SCALE[index] ?? 'equilibrado');
  }

  intensityScale(): number {
    return Math.max(0, INTENSITY_SCALE.indexOf(this.intensity()));
  }

  private bestArchetypeForPrompt(prompt: string): AgentArchetype | null {
    return this.bestScoredMatch(this.archetypes(), prompt, (archetype) => [
      archetype.name,
      archetype.key,
      archetype.description ?? '',
      readString(archetype.defaultGoalPolicy, 'northStar'),
    ]);
  }

  private selectSuggestedKnowledgeBasesForPrompt(prompt: string): void {
    const selected = new Set(this.selectedKnowledgeBaseIds());
    for (const base of this.scoredMatches(this.knowledgeBases(), prompt, (item) => [
      item.name,
      item.description ?? '',
      item.type,
      this.knowledgeTypeLabel(item.type),
    ]).slice(0, 4)) {
      selected.add(base.id);
    }
    this.selectedKnowledgeBaseIds.set([...selected]);
    this.knowledgeBasesTouched.set(true);
  }

  private selectSuggestedIntegrationsForPrompt(prompt: string): void {
    const selected = new Set(this.selectedIntegrationIds());
    for (const integration of this.scoredMatches(this.integrations(), prompt, (item) => [
      item.displayName,
      item.provider,
      item.status,
    ]).slice(0, 3)) {
      selected.add(integration.id);
    }
    this.selectedIntegrationIds.set([...selected]);
  }

  private bestScoredMatch<T>(
    items: readonly T[],
    prompt: string,
    texts: (item: T) => readonly string[],
  ): T | null {
    return this.scoredMatches(items, prompt, texts)[0] ?? null;
  }

  private scoredMatches<T>(
    items: readonly T[],
    prompt: string,
    texts: (item: T) => readonly string[],
  ): T[] {
    const tokens = promptTokens(prompt);
    if (tokens.length === 0) return [];
    return items
      .map((item) => ({ item, score: scorePromptMatch(tokens, texts(item).join(' ')) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.item);
  }

  private nodeModelValue(node: string): string {
    return readString(this.nodeModelPolicyFor(node), 'model') || this.firstArchetypeModel();
  }

  setLibraryTypeFilter(value: unknown): void {
    if (typeof value === 'string') {
      this.libraryTypeFilter.set(value);
    }
  }

  setDeploymentMode(value: unknown): void {
    if (isDeploymentMode(value)) {
      this.deploymentMode.set(value);
      this.markDeploymentDirty();
    }
  }

  setDeploymentStatus(value: unknown): void {
    if (isDeploymentStatus(value)) {
      this.deploymentStatus.set(value);
      this.markDeploymentDirty();
    }
  }

  setDeploymentSegmentForAccount(accountId: string, value: unknown): void {
    const segmentId = typeof value === 'string' && value.trim() ? value.trim() : null;
    this.deploymentSegmentByAccountId.update((segments) => ({
      ...segments,
      [accountId]: segmentId,
    }));
    this.markDeploymentDirty();
  }

  deploymentSegmentForAccount(accountId: string): string {
    return this.deploymentSegmentByAccountId()[accountId] ?? '';
  }

  setVersionResolution(value: unknown): void {
    if (isVersionResolution(value)) {
      this.versionResolution.set(value);
      this.markDeploymentDirty();
    }
  }

  setDeploymentModeForAccount(accountId: string, value: unknown): void {
    if (!isDeploymentMode(value)) return;
    this.deploymentModeByAccountId.update((modes) => ({ ...modes, [accountId]: value }));
    this.markDeploymentDirty();
  }

  deploymentModeForAccount(accountId: string): AgentBuilderDeploymentMode {
    return this.deploymentModeByAccountId()[accountId] ?? this.deploymentMode();
  }

  setDeploymentStatusForAccount(accountId: string, value: unknown): void {
    if (!isDeploymentStatus(value)) return;
    this.deploymentStatusByAccountId.update((statuses) => ({ ...statuses, [accountId]: value }));
    this.markDeploymentDirty();
  }

  deploymentStatusForAccount(accountId: string): AgentBuilderDeploymentStatus {
    return this.deploymentStatusByAccountId()[accountId] ?? this.deploymentStatus();
  }

  setDeploymentVersionResolutionForAccount(accountId: string, value: unknown): void {
    if (!isVersionResolution(value)) return;
    this.deploymentVersionResolutionByAccountId.update((versions) => ({
      ...versions,
      [accountId]: value,
    }));
    if (value !== 'fixed_agent_version') {
      this.deploymentAgentVersionIdByAccountId.update((ids) => ({ ...ids, [accountId]: null }));
    } else if (!this.deploymentAgentVersionIdByAccountId()[accountId]) {
      const fallbackVersionId = this.currentVersion()?.id ?? this.publishedVersions()[0]?.id ?? null;
      this.deploymentAgentVersionIdByAccountId.update((ids) => ({
        ...ids,
        [accountId]: fallbackVersionId,
      }));
    }
    this.markDeploymentDirty();
  }

  deploymentVersionResolutionForAccount(accountId: string): AgentBuilderVersionResolution {
    return this.deploymentVersionResolutionByAccountId()[accountId] ?? this.versionResolution();
  }

  setDeploymentAgentVersionForAccount(accountId: string, value: unknown): void {
    const versionId = typeof value === 'string' && value.trim() ? value.trim() : null;
    this.deploymentAgentVersionIdByAccountId.update((ids) => ({ ...ids, [accountId]: versionId }));
    this.markDeploymentDirty();
  }

  deploymentAgentVersionForAccount(accountId: string): string {
    return this.deploymentAgentVersionIdByAccountId()[accountId] ?? '';
  }

  deploymentVersionSummary(accountId: string): string {
    const resolution = this.deploymentVersionResolutionForAccount(accountId);
    if (resolution === 'fixed_agent_version') {
      const version = this.versionById(this.deploymentAgentVersionIdByAccountId()[accountId] ?? null);
      return version ? `Fixa em v${version.version}` : 'Escolha uma versão publicada';
    }
    if (resolution === 'current_agent_version') {
      const version = this.currentVersion();
      return version ? `Versão atual: v${version.version}` : 'Sem versão atual publicada';
    }
    const latest = this.latestPublishedVersion();
    return latest ? `Última publicada: v${latest.version}` : 'Sem versão publicada';
  }

  updateMissionBlock(id: string, key: 'title' | 'body', event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.updateMissionBlockText(id, key, value);
  }

  updateMissionBlockText(id: string, key: 'title' | 'body', value: string): void {
    const document = this.missionDocument();
    if (!document) return;
    const blocks = document.blocks.map((block) => {
      if (block.id !== id) return block;
      const next = { ...block, [key]: value };
      return key === 'body' ? this.withParsedMissionReferences(next) : next;
    });
    this.missionDocument.set({
      ...document,
      blocks,
      references: this.collectMissionReferences(blocks, document.references),
    });
    this.markDirty();
  }

  updateMissionBlockType(id: string, type: AgentMissionBlock['type']): void {
    const document = this.missionDocument();
    if (!document) return;
    const title = missionBlockDefaultTitle(type);
    this.missionDocument.set({
      ...document,
      blocks: document.blocks.map((block) =>
        block.id === id
          ? {
              ...block,
              type,
              title: block.title.trim() || title,
              slug: block.slug.trim() || slugify(title),
            }
          : block,
      ),
    });
    this.markDirty();
  }

  updateMissionDocumentTitle(value: string): void {
    const document = this.missionDocument() ?? this.emptyMissionDocument();
    this.missionDocument.set({
      ...document,
      title: value.trim() || 'Missão do agente',
    });
    this.markDirty();
  }

  replaceMissionFromEditor(snapshot: MissionEditorSnapshot): void {
    const document = sanitizeMissionDocument(snapshot.document);
    this.missionDocument.set(document);
    this.missionDiagnostics.set(document.diagnostics);
    this.compiledPrompt.set(snapshot.text);
    this.syncCollectionFieldsFromMission(document);
    this.markDirty();
  }

  addMissionBlock(
    type: 'stage' | 'policy' | 'handoff' | 'note' | 'retrieval' | 'collection',
    afterBlockId: string | null = null,
  ): void {
    const document = this.missionDocument() ?? this.emptyMissionDocument();
    const title = missionBlockDefaultTitle(type);
    const block: AgentMissionBlock = {
      id: nextLocalId('block'),
      type,
      slug: slugify(title),
      title,
      body: missionBlockDefaultBody(type),
      fields: {},
      references: [],
      diagnostics: [],
    };
    const insertIndex = afterBlockId
      ? document.blocks.findIndex((item) => item.id === afterBlockId) + 1
      : document.blocks.length;
    const blocks =
      insertIndex > 0 && insertIndex < document.blocks.length
        ? [...document.blocks.slice(0, insertIndex), block, ...document.blocks.slice(insertIndex)]
        : [...document.blocks, block];
    this.missionDocument.set({
      ...document,
      blocks,
    });
    this.markDirty();
  }

  createCollectionField(label: string): BuilderCollectionField | null {
    const normalizedLabel = label.trim().replace(/\s+/g, ' ');
    if (!normalizedLabel) return null;

    const field: BuilderCollectionField = {
      id: nextLocalId('field'),
      label: normalizedLabel,
      key: this.uniqueCollectionFieldKey(normalizedLabel),
      source: 'conversation',
      required: true,
      writeToMemory: true,
      description: 'Criado no editor para o agente coletar durante esta etapa.',
    };
    this.collectionFields.update((fields) => [...fields, field]);
    this.markDirty();
    return field;
  }

  private syncCollectionFieldsFromMission(document: AgentMissionDocument): void {
    const existingKeys = new Set(this.collectionFields().map((field) => field.key));
    const fields = document.references.flatMap((reference): BuilderCollectionField[] => {
      if (reference.type !== 'field' || !reference.targetId || existingKeys.has(reference.targetId)) {
        return [];
      }
      existingKeys.add(reference.targetId);
      const label = missionFieldLabel(reference) || collectionFieldLabelFromKey(reference.targetId);
      return [
        {
          id: nextLocalId('field'),
          label,
          key: reference.targetId,
          source: collectionFieldSource(reference.targetId.split('.')[0] ?? 'conversation'),
          required: true,
          writeToMemory: true,
          description: 'Criado no editor para o agente coletar durante esta etapa.',
        },
      ];
    });
    if (fields.length === 0) return;
    this.collectionFields.update((current) => [...current, ...fields]);
  }

  removeCollectionField(fieldId: string): void {
    this.collectionFields.update((fields) => fields.filter((field) => field.id !== fieldId));
    this.markDirty();
  }

  removeMissionBlock(id: string): void {
    const document = this.missionDocument();
    if (!document) return;
    this.missionDocument.set({
      ...document,
      blocks: document.blocks.filter((block) => block.id !== id),
    });
    this.markDirty();
  }

  startMissionGeneration(): void {
    if (
      !this.agentName().trim() ||
      !this.intent().trim() ||
      this.missionRunStatus() === 'running' ||
      this.missionRunStatus() === 'queued'
    )
      return;

    this.prepareMissionGenerationState();
    this.saveIncremental(false)
      .pipe(
        switchMap(() => {
          const agentId = this.agentId();
          if (!agentId) throw new Error('agent_missing');
          return this.api.startBuilderMissionRun(agentId, this.buildGenerationContext());
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.stopMissionRunTracking();
          this.missionRunStatus.set('failed');
          this.streamErrors.update((errors) => [
            ...errors,
            'Não foi possível iniciar a geração da missão.',
          ]);
          return of(null);
        }),
      )
      .subscribe((run) => {
        if (!run) return;
        this.missionRunId.set(run.runId);
        this.missionRunStatus.set(run.status === 'queued' ? 'queued' : 'running');
        this.realtime.subscribe(run.runId);
        this.missionRunEventsSubscription?.unsubscribe();
        this.missionRunEventsSubscription = this.realtime
          .events()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((event) => this.applyRunEvent(event));
        this.startMissionRunPolling(run.runId, run.agentId);
      });
  }

  validateMissionAndSave(): void {
    const document = this.editableMissionDocument();
    if (!this.missionDocument()) this.missionDocument.set(document);
    this.validatingMission.set(true);
    this.api
      .validateMission({
        missionDocument: document,
        missionSourceDsl: this.missionSourceDsl() || undefined,
        referenceCatalog: this.referenceCatalog(),
      })
      .pipe(
        switchMap((validation) => {
          this.missionDiagnostics.set(validation.diagnostics);
          if (validation.document) this.missionDocument.set(validation.document);
          this.compiledPrompt.set(validation.compiledPrompt ?? this.compileMissionPrompt());
          this.versionDraftPatch.set(asRecord(validation.versionDraftPatch));
          return this.saveIncremental(false);
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toast.error('Não foi possível validar a missão.');
          return of(null);
        }),
      )
      .subscribe((version) => {
        this.validatingMission.set(false);
        if (!version) return;
        this.toast.success('Missão salva', 'O rascunho da versão foi atualizado.');
        this.suggestTriggers();
      });
  }

  saveDraft(): void {
    this.saveIncremental(false)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toast.error('Não foi possível salvar o rascunho.');
          return of(null);
        }),
      )
      .subscribe();
  }

  saveAndExitRequest(): Observable<unknown> {
    this.savingExit.set(true);
    return this.saveIncremental(true).pipe(
      map((version) => {
        this.savingExit.set(false);
        return version;
      }),
      catchError((error: unknown) => {
        this.savingExit.set(false);
        throw error;
      }),
    );
  }

  publish(): void {
    if (!this.canPublish()) {
      this.toast.error('Não é possível publicar agora.', this.publishBlockers()[0]);
      return;
    }
    this.publishing.set(true);
    this.validateMissionForPublish()
      .pipe(
        switchMap((valid) => (valid ? this.saveIncremental(false) : of(null))),
        switchMap((version) => {
          if (!version) return of(null);
          const agentId = this.agentId();
          if (!agentId) throw new Error('agent_missing');
          return this.api
            .publishVersion(
              agentId,
              version.id,
              { expectedDraftVersion: version.version },
              createIdempotencyKey(),
            )
            .pipe(
              switchMap((publishedVersion) =>
                forkJoin({
                  deployments: this.persistDeployments(agentId),
                  triggers: this.persistAcceptedTriggers(agentId),
                }).pipe(map(() => publishedVersion)),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toast.error(
            'Não foi possível publicar o agente.',
            'Revise os bloqueios e tente novamente.',
          );
          return of(null);
        }),
      )
      .subscribe((version) => {
        this.publishing.set(false);
        if (!version) return;
        this.toast.success(
          'Agente publicado e operação atualizada',
          'A versão e os vínculos de operação foram aplicados.',
        );
        this.versions.update((versions) => upsertVersion(versions, version));
        this.agentDirty.set(false);
        this.versionDirty.set(false);
        this.deploymentDirty.set(false);
        this.triggerDirty.set(false);
        this.dirty.set(false);
      });
  }

  private validateMissionForPublish(): Observable<boolean> {
    const document = this.missionDocument();
    if (!document) return of(false);
    return this.api
      .validateMission({
        missionDocument: document,
        missionSourceDsl: this.missionSourceDsl() || undefined,
        referenceCatalog: this.referenceCatalog(),
      })
      .pipe(
        map((validation) => {
          this.missionDiagnostics.set(validation.diagnostics);
          if (validation.document) this.missionDocument.set(validation.document);
          this.compiledPrompt.set(validation.compiledPrompt ?? this.compileMissionPrompt());
          this.versionDraftPatch.set(asRecord(validation.versionDraftPatch));
          const blocked =
            !validation.publishable ||
            validation.diagnostics.some((diagnostic) => diagnostic.severity === 'error');
          if (blocked) {
            this.toast.error(
              'A missão tem bloqueios antes da publicação.',
              'Corrija os diagnósticos e tente novamente.',
            );
          }
          return !blocked;
        }),
        catchError(() => {
          this.toast.error('Não foi possível validar a missão.');
          return of(false);
        }),
      );
  }

  saveDeploymentsAndAcceptedTriggers(): Observable<unknown> {
    const agentId = this.agentId();
    if (!agentId) return of(null);
    return this.persistAcceptedTriggers(agentId).pipe(
      switchMap(() => this.persistDeployments(agentId)),
    );
  }

  saveDeploymentConfiguration(): void {
    const agentId = this.agentId();
    if (!agentId) {
      this.toast.error('Salve o agente antes de configurar deployments.');
      return;
    }
    this.saving.set(true);
    this.saveDeploymentsAndAcceptedTriggers()
      .pipe(
        switchMap(() => this.api.listDeployments(agentId)),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.toast.error('Não foi possível salvar os deployments.');
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.saving.set(false);
        if (!response) return;
        this.deployments.set(response.data);
        this.applyDeployments(response.data);
        this.deploymentDirty.set(false);
        this.triggerDirty.set(false);
        this.dirty.set(this.agentDirty() || this.versionDirty());
        this.toast.success('Deployments salvos.');
      });
  }

  acceptSuggestion(suggestion: AgentBuilderTriggerSuggestion): void {
    const existing = this.triggerDrafts().find((draft) => draft.id === suggestion.key);
    if (existing) {
      this.triggerDrafts.update((drafts) =>
        drafts.map((draft) =>
          draft.id === suggestion.key
            ? {
                ...draft,
                accepted: true,
              }
            : draft,
        ),
      );
      this.markTriggerDirty();
      return;
    }
    this.triggerDrafts.update((drafts) => [
      ...drafts,
      {
        id: suggestion.key,
        name: suggestion.title,
        eventKey: suggestion.eventKey,
        condition:
          suggestion.notes[0] ??
          `Evento ${suggestion.canonicalEvent} atende aos critérios da missão.`,
        action: 'automation',
        mode: 'semantic',
        accepted: true,
      },
    ]);
    this.markTriggerDirty();
  }

  toggleTriggerAccepted(id: string): void {
    this.triggerDrafts.update((drafts) =>
      drafts.map((draft) => (draft.id === id ? { ...draft, accepted: !draft.accepted } : draft)),
    );
    this.markTriggerDirty();
  }

  knowledgeTypeLabel(type: string | null | undefined): string {
    if (!type) return 'Biblioteca';
    return KNOWLEDGE_TYPE_LABELS[type] ?? type;
  }

  libraryTypeFilterLabel(): string {
    return (
      this.knowledgeTypeOptions().find((option) => option.value === this.libraryTypeFilter())
        ?.label ?? 'Tipo'
    );
  }

  toneLabel(): string {
    return this.toneOption().label;
  }

  intensityLabel(): string {
    return this.intensityOption().label;
  }

  deploymentModeLabel(): string {
    return (
      DEPLOYMENT_MODE_OPTIONS.find((option) => option.value === this.deploymentMode())?.label ??
      this.deploymentMode()
    );
  }

  messageLengthLabel(value: ChannelBehaviorDraft['messageLength']): string {
    const labels: Record<ChannelBehaviorDraft['messageLength'], string> = {
      curta: 'Curta',
      normal: 'Normal',
      detalhada: 'Detalhada',
    };
    return labels[value];
  }

  responseLength(): ChannelBehaviorDraft['messageLength'] {
    const selectedType = this.selectedChannelTypes()[0] ?? 'whatsapp';
    return this.channelBehaviors()[selectedType].messageLength;
  }

  private rangeIndex(event: Event, length: number): number {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isFinite(value)) return 0;
    return Math.min(Math.max(Math.round(value), 0), Math.max(0, length - 1));
  }

  deploymentStatusLabel(): string {
    return (
      DEPLOYMENT_STATUS_OPTIONS.find((option) => option.value === this.deploymentStatus())?.label ??
      this.deploymentStatus()
    );
  }

  versionResolutionLabel(): string {
    return (
      VERSION_RESOLUTION_OPTIONS.find((option) => option.value === this.versionResolution())
        ?.label ?? this.versionResolution()
    );
  }

  channelLabel(kind: string | null | undefined): string {
    if (!kind) return 'Canal';
    return CHANNEL_LABELS[kind] ?? kind;
  }

  deploymentSegmentLabel(accountId: string): string {
    const segmentId = this.deploymentSegmentByAccountId()[accountId];
    if (!segmentId) return 'Atender todos os contatos';
    return this.contactSegments().find((segment) => segment.id === segmentId)?.name ?? 'Segmento removido ou sem acesso';
  }

  deploymentSegmentSummary(accountId: string): string {
    const segmentId = this.deploymentSegmentByAccountId()[accountId];
    if (!segmentId) return 'O agente pode atender qualquer contato que chegar nesta conta.';
    const segment = this.contactSegments().find((item) => item.id === segmentId);
    if (!segment) return 'Referência removida ou sem acesso: publicação será bloqueada.';
    return `Atende somente contatos do segmento ${segment.name}.`;
  }

  channelAccountCountForType(type: AgentChannelConfigChannelTypeEnumKey): number {
    return this.channelAccounts().filter(
      (account) => this.toAgentChannelType(account.kind) === type,
    ).length;
  }

  channelAccountsForType(type: AgentChannelConfigChannelTypeEnumKey): ChannelAccount[] {
    return this.channelAccounts().filter(
      (account) => this.toAgentChannelType(account.kind) === type,
    );
  }

  integrationStatusLabel(status: string | null | undefined): string {
    const labels: Record<string, string> = {
      connected: 'Conectada',
      active: 'Ativa',
      pending: 'Pendente',
      error: 'Com erro',
      disabled: 'Desativada',
      revoked: 'Revogada',
    };
    return status ? (labels[status] ?? status) : 'Sem status';
  }

  toolRiskLabel(risk: string | null | undefined): string {
    const labels: Record<string, string> = {
      read: 'Somente leitura',
      low: 'Baixo impacto',
      medium: 'Requer critério',
      high: 'Sensível',
      irreversible: 'Irreversível',
    };
    return risk ? (labels[risk] ?? risk) : 'Uso controlado';
  }

  isSelected(ids: readonly string[], id: string): boolean {
    return ids.includes(id);
  }

  isSelectedChannelType(type: AgentChannelConfigChannelTypeEnumKey): boolean {
    return this.selectedChannelTypes().includes(type);
  }

  stepState(stepId: AgentBuilderStepId): 'active' | 'complete' | 'disabled' | 'available' {
    const index = this.steps.findIndex((step) => step.id === stepId);
    if (this.activeStep() === stepId) return 'active';
    if (index < this.furthestStepIndex()) return 'complete';
    if (index > this.furthestStepIndex()) return 'disabled';
    return 'available';
  }

  markDirty(): void {
    this.agentDirty.set(true);
    this.versionDirty.set(true);
    this.dirty.set(true);
  }

  markDeploymentDirty(): void {
    this.deploymentDirty.set(true);
    this.dirty.set(true);
  }

  markTriggerDirty(): void {
    this.triggerDirty.set(true);
    this.dirty.set(true);
  }

  versionById(versionId: string | null | undefined): AgentVersion | null {
    if (!versionId) return null;
    return this.versions().find((version) => version.id === versionId) ?? null;
  }

  latestPublishedVersion(): AgentVersion | null {
    return this.publishedVersions()
      .slice()
      .sort((a, b) => b.version - a.version)[0] ?? null;
  }

  private loadExistingAgentDetails(agentId: string): Observable<unknown> {
    return forkJoin({
      versions: this.api
        .listVersions(agentId)
        .pipe(this.withPartialLoadWarning('versões')),
      channels: this.api
        .listChannelConfigs(agentId)
        .pipe(this.withPartialLoadWarning('canais')),
      goals: this.api.listGoals(agentId).pipe(this.withPartialLoadWarning('metas')),
      deployments: this.api
        .listDeployments(agentId)
        .pipe(this.withPartialLoadWarning('deployments')),
      triggerPolicies: this.api
        .listTriggerPolicies()
        .pipe(this.withPartialLoadWarning('políticas de gatilho')),
      triggerAssignments: this.api
        .listTriggerPolicyAssignments(agentId)
        .pipe(this.withPartialLoadWarning('gatilhos aceitos')),
      runs: this.api
        .listRuns({ agentId, pageSize: 1 })
        .pipe(this.withPartialLoadWarning('histórico de execução')),
    }).pipe(
      map((details) => {
        this.versions.set(details.versions.data);
        const draft = selectEditableVersion(
          details.versions.data,
          this.selectedAgent()?.currentVersionId ?? null,
        );
        if (draft) this.applyVersion(draft);
        this.applyChannelConfigs(details.channels.data);
        this.applyTriggerPolicyAssignments(
          details.triggerAssignments.data,
          details.triggerPolicies.data,
        );
        this.deployments.set(details.deployments.data);
        this.applyDeployments(details.deployments.data);
        this.hasExecutionHistory.set(details.runs.data.length > 0);
        return details.goals;
      }),
    );
  }

  private applyDeployments(deployments: AgentDeployment[]): void {
    const segmentByAccountId: Record<string, string | null> = {};
    const modeByAccountId: Record<string, AgentBuilderDeploymentMode> = {};
    const statusByAccountId: Record<string, AgentBuilderDeploymentStatus> = {};
    const versionResolutionByAccountId: Record<string, AgentBuilderVersionResolution> = {};
    const agentVersionIdByAccountId: Record<string, string | null> = {};
    const selectedAccountIds: string[] = [];
    for (const deployment of deployments) {
      const accountId = this.deploymentChannelAccountId(deployment);
      if (!accountId) continue;
      if (deployment.status !== 'disabled') selectedAccountIds.push(accountId);
      segmentByAccountId[accountId] = this.deploymentSegmentRefId(deployment);
      statusByAccountId[accountId] = isDeploymentStatus(deployment.status)
        ? deployment.status
        : this.deploymentStatus();
      const policy = asRecord(deployment.conversationExecutionPolicy);
      const mode = readString(policy, 'mode');
      modeByAccountId[accountId] = isDeploymentMode(mode) ? mode : this.deploymentMode();
      versionResolutionByAccountId[accountId] = isVersionResolution(deployment.versionResolution)
        ? deployment.versionResolution
        : this.versionResolution();
      agentVersionIdByAccountId[accountId] = deployment.agentVersionId ?? deployment.resolvedAgentVersionId ?? null;
    }
    this.selectedChannelAccountIds.set([...new Set(selectedAccountIds)]);
    this.selectedChannelTypes.set(this.channelTypesFromAccountIds(selectedAccountIds));
    this.deploymentSegmentByAccountId.set(segmentByAccountId);
    this.deploymentModeByAccountId.set(modeByAccountId);
    this.deploymentStatusByAccountId.set(statusByAccountId);
    this.deploymentVersionResolutionByAccountId.set(versionResolutionByAccountId);
    this.deploymentAgentVersionIdByAccountId.set(agentVersionIdByAccountId);
  }

  private withPartialLoadWarning<T>(
    label: string,
    fallback?: T,
  ) {
    return (source: Observable<T>): Observable<T> =>
      source.pipe(
        catchError(() => {
          this.loadWarnings.update((warnings) => [
            ...warnings,
            `Não foi possível carregar ${label}.`,
          ]);
          return of(fallback ?? ({ data: [] } as unknown as T));
        }),
      );
  }

  private applyAgent(agent: Agent): void {
    this.agentId.set(agent.id);
    this.selectedArchetypeId.set(agent.archetypeId ?? null);
    this.agentName.set(agent.name);
    this.intent.set(agent.objective || agent.goal || agent.description || '');
    const persona = asRecord(agent.persona);
    this.presentationName.set(readString(persona, 'builderPresentationName') || agent.name);
    const segmentation = asRecord(agent.segmentationPolicy);
    const tone = readString(persona, 'builderTone');
    const intensity = readString(persona, 'builderIntensity');
    if (isTone(tone)) this.tone.set(tone);
    if (isIntensity(intensity)) this.intensity.set(intensity);
    const selectedAccountIds = readStringArray(segmentation, 'selectedChannelAccountIds');
    const storedChannelTypes = this.coerceChannelTypes(
      readStringArray(segmentation, 'selectedChannelTypes'),
    );
    this.selectedChannelAccountIds.set(selectedAccountIds);
    this.selectedChannelTypes.set(
      storedChannelTypes.length
        ? storedChannelTypes
        : this.channelTypesFromAccountIds(selectedAccountIds),
    );
  }

  private applyVersion(version: AgentVersion): void {
    this.sourceVersionId.set(version.id);
    this.selectedKnowledgeBaseIds.set(
      (version.knowledgeBases ?? []).map((base) => base.knowledgeBaseId),
    );
    this.knowledgeBasesTouched.set(false);
    this.selectedToolIds.set((version.toolAllowlist ?? []).map((tool) => tool.toolDefinitionId));
    this.toolsTouched.set(false);
    this.toolPolicies.set(
      Object.fromEntries(
        (version.toolAllowlist ?? []).map((tool) => [
          tool.toolDefinitionId,
          normalizeToolPolicyDraft(tool.argumentPolicy),
        ]),
      ),
    );
    this.selectedSkillIds.set((version.skillAllowlist ?? []).map((skill) => skill.agentSkillId));
    this.skillsTouched.set(false);
    const modelConfig = asRecord(version.modelConfig);
    const builder = asRecord(modelConfig['builder']);
    const document = builder['missionDocument'];
    if (isMissionDocument(document) && (document.blocks.length > 0 || !version.systemPrompt?.trim())) {
      this.missionDocument.set(sanitizeMissionDocument(document));
    } else if (version.systemPrompt?.trim()) {
      this.missionDocument.set(this.legacyPromptMissionDocument(version.systemPrompt));
    }
    const sourceDsl = readString(builder, 'missionSourceDsl');
    if (sourceDsl) this.missionSourceDsl.set(sourceDsl);
    const storedCollectionFields = coerceCollectionFields(
      version.memoryPolicy?.['collectionFields'],
    );
    if (storedCollectionFields.length > 0) this.collectionFields.set(storedCollectionFields);
    const agentBindings = asRecord(modelConfig['agentBindings']);
    this.subAccountBindings.set(coerceStringMap(agentBindings['subAccountBindings']));
    const eventTemplate = asRecord(agentBindings['eventTemplate']);
    this.eventTemplate.set({
      titleDefault: typeof eventTemplate['titleDefault'] === 'string' ? eventTemplate['titleDefault'] : '',
      durationMinutes: typeof eventTemplate['durationMinutes'] === 'number' ? eventTemplate['durationMinutes'] : null,
    });
    this.agendaVisibility.set(agentBindings['agendaVisibility'] === 'internal' ? 'internal' : 'client');
    this.compiledPrompt.set(version.systemPrompt ?? '');
  }

  private applyChannelConfigs(
    configs: readonly {
      channelType: AgentChannelConfigChannelTypeEnumKey;
      enabled: boolean;
      behaviorPolicy: Record<string, unknown>;
    }[],
  ): void {
    const next = { ...this.channelBehaviors() };
    const selectedTypes: AgentChannelConfigChannelTypeEnumKey[] = [];
    for (const config of configs) {
      const policy = asRecord(config.behaviorPolicy);
      const toneStylePolicy = asRecord(policy['toneStylePolicy']);
      const messageLengthPolicy = asRecord(policy['messageLengthPolicy']);
      if (config.enabled) selectedTypes.push(config.channelType);
      next[config.channelType] = {
        ...next[config.channelType],
        enabled: config.enabled,
        replyMode: isDeploymentMode(readString(policy, 'replyMode'))
          ? (readString(policy, 'replyMode') as AgentBuilderDeploymentMode)
          : next[config.channelType].replyMode,
        toneStyle: readString(toneStylePolicy, 'style') || next[config.channelType].toneStyle,
        messageLength: isMessageLength(messageLengthPolicy['level'])
          ? messageLengthPolicy['level']
          : next[config.channelType].messageLength,
      };
    }
    this.channelBehaviors.set(next);
    if (selectedTypes.length) this.selectedChannelTypes.set(this.sortChannelTypes(selectedTypes));
  }

  private applyTriggerPolicyAssignments(
    assignments: readonly AgentTriggerPolicyAssignmentRecord[],
    policies: readonly AgentTriggerPolicy[],
  ): void {
    const policiesById = new Map(policies.map((policy) => [policy.id, policy]));
    const drafts = assignments
      .filter((assignment) => assignment.status !== 'archived')
      .sort((a, b) => a.sequence - b.sequence)
      .map((assignment) => {
        const policy = policiesById.get(assignment.triggerPolicyId);
        const rule = policy?.rules?.[0];
        const scopePolicy = asRecord(policy?.scopePolicy);
        const evaluationPolicy = asRecord(policy?.evaluationPolicy);
        const conditionPolicy = asRecord(rule?.conditionPolicy);
        const actionPolicy = asRecord(rule?.actionPolicy);
        const actions = Array.isArray(actionPolicy['actions']) ? actionPolicy['actions'] : [];
        const firstAction = asRecord(actions[0]);
        const eventKey =
          readString(scopePolicy, 'eventKey') ||
          readString(conditionPolicy, 'eventKey') ||
          policy?.name ||
          assignment.triggerPolicyId;
        const action = readString(firstAction, 'action');
        const mode =
          readString(evaluationPolicy, 'mode') ||
          readString(conditionPolicy, 'mode') ||
          (rule?.conditionType === 'declarative' ? 'deterministic' : 'semantic');
        const draftId =
          readString(scopePolicy, 'builderDraftId') ||
          readString(assignment.overridePolicy, 'builderDraftId') ||
          eventKey;

        return {
          id: draftId,
          triggerPolicyId: assignment.triggerPolicyId,
          name: policy?.name || eventKey,
          eventKey,
          condition: readString(conditionPolicy, 'condition') || policy?.name || eventKey,
          action: isTriggerAction(action) ? action : 'automation',
          mode: isTriggerMode(mode) ? mode : 'semantic',
          accepted: assignment.status === 'active',
        } satisfies BuilderTriggerDraft;
      });

    this.triggerDrafts.set(drafts);
  }

  private advanceTo(index: number): void {
    this.furthestStepIndex.update((current) => Math.max(current, index));
    this.activeStep.set(this.steps[index]!.id);
  }

  private ensureAgentRoot(): Observable<Agent> {
    this.saving.set(true);
    const existingId = this.agentId();
    const request = this.buildAgentRequest({ includeDraftStatus: !existingId });
    const request$ = existingId
      ? this.api.updateAgent(existingId, request)
      : this.api.createAgent(request);

    return request$.pipe(
      switchMap((agent) => {
        this.applyAgent(agent);
        return this.api
          .replaceChannelConfigs(agent.id, this.buildChannelConfigs())
          .pipe(map(() => agent));
      }),
      map((agent) => {
        this.saving.set(false);
        this.dirty.set(false);
        return agent;
      }),
      catchError((error: unknown) => {
        this.saving.set(false);
        throw error;
      }),
    );
  }

  private saveIncremental(silent: boolean): Observable<AgentVersion> {
    this.saving.set(true);
    return this.ensureAgentRoot().pipe(
      switchMap((agent) =>
        forkJoin({
          goals: this.api.replaceGoals(agent.id, this.buildGoals(agent.id)),
          version: this.api.upsertDraftVersion(agent.id, this.buildVersionDraftRequest()),
        }),
      ),
      map((result) => {
        this.saving.set(false);
        this.agentDirty.set(false);
        this.versionDirty.set(false);
        this.versions.update((versions) => upsertVersion(versions, result.version));
        this.dirty.set(this.deploymentDirty() || this.triggerDirty());
        if (!silent)
          this.toast.success('Rascunho salvo', 'O construtor salvou a configuração incremental.');
        return result.version;
      }),
      catchError((error: unknown) => {
        this.saving.set(false);
        throw error;
      }),
    );
  }

  private buildAgentRequest(options: { includeDraftStatus?: boolean } = {}): AgentUpsertRequest {
    const archetype = this.selectedArchetype();
    const requestedName =
      this.agentName().trim() || (archetype ? `Agente ${archetype.name}` : 'Novo agente');
    const name = this.agentId() ? requestedName : this.nextAvailableAgentName(requestedName);
    const presentationName = this.ensurePresentationName();
    const intent =
      this.intent().trim() ||
      archetype?.description ||
      'Atender clientes com clareza, segurança e rastreabilidade.';
    const request: AgentUpsertRequest = {
      archetypeId: this.selectedArchetypeId(),
      name,
      objective: intent,
      goal: intent,
      description: 'Agente configurado pelo Agent Builder.',
      persona: {
        operatingRole: archetype?.key ?? 'customer_support_agent',
        builderPresentationName: presentationName,
        archetypeGraphKey: archetype?.defaultGraphKey ?? this.selectedGraph()?.key,
        archetypeGraphVersion: archetype?.defaultGraphVersion ?? this.selectedGraph()?.version,
        archetypeNodePrompts: archetype?.defaultNodePrompts ?? {},
        archetypeNodeModelPolicy: archetype?.defaultNodeModelPolicy ?? {},
        builderTone: this.tone(),
        builderIntensity: this.intensity(),
        tonePolicy: this.toneOption().policy,
        intensityPolicy: this.intensityOption().policy,
      },
      segmentationPolicy: {
        configuredFrom: 'agent_builder',
        selectedChannelTypes: this.selectedChannelTypes(),
        selectedChannelAccountIds: this.selectedChannelAccountIds(),
        selectedIntegrationIds: this.selectedIntegrationIds(),
      },
    };
    if (options.includeDraftStatus) request.status = 'draft';
    return request;
  }

  private nextAvailableAgentName(baseName: string): string {
    const currentAgentId = this.agentId();
    const usedNames = new Set(
      this.agents()
        .filter((agent) => agent.id !== currentAgentId)
        .map((agent) => normalizeAgentName(agent.name)),
    );
    if (!usedNames.has(normalizeAgentName(baseName))) return baseName;

    for (let suffix = 2; suffix < 1000; suffix += 1) {
      const candidate = `${baseName} ${suffix}`;
      if (!usedNames.has(normalizeAgentName(candidate))) return candidate;
    }

    return `${baseName} ${Date.now().toString(36).slice(-4)}`;
  }

  private buildGenerationContext(): AgentBuilderContextRequest {
    const presentationName = this.ensurePresentationName();
    return {
      agentName: this.agentName().trim(),
      selectedArchetype: this.selectedArchetype()
        ? this.slimArchetype(this.selectedArchetype()!)
        : undefined,
      channelAccounts: this.selectedChannelAccounts().map((account) => ({
        id: account.id,
        kind: account.kind,
        label: account.label,
        integrationId: account.integrationId,
      })),
      knowledgeBases: this.selectedKnowledgeBases().map((base) => ({
        id: base.id,
        name: base.name,
        type: base.type,
        description: base.description,
      })),
      integrations: this.selectedIntegrations().map((integration) => ({
        id: integration.id,
        provider: integration.provider,
        displayName: integration.displayName,
        status: integration.status,
      })),
      tools: this.selectedTools().map((tool) => ({
        id: tool.id,
        key: tool.key,
        displayName: tool.displayName,
        description: tool.description,
        riskLevel: tool.riskLevel,
        integrationId: tool.integrationId,
        inputSchema: tool.inputSchema,
        outputSchema: tool.outputSchema,
        argumentPolicy: this.toolPolicies()[tool.id] ?? emptyToolPolicyDraft(),
      })),
      skills: this.selectedSkills().map((skill) => ({
        id: skill.id,
        key: skill.key,
        name: skill.name,
        currentVersionId: skill.currentVersionId,
      })),
      triggerCandidates: this.triggerSuggestions(),
      tone: this.toneOption().label,
      intensity: this.intensityOption().label,
      intent: this.intent().trim(),
      referenceCatalog: this.referenceCatalog(),
      model: this.nodeModelValue(BUILDER_GENERATION_MODEL_NODE),
      builderState: {
        step: this.activeStep(),
        graph: this.selectedGraph()
          ? {
              key: this.selectedGraph()!.key,
              version: this.selectedGraph()!.version,
            }
          : null,
        archetypeNodePrompts: this.selectedArchetype()?.defaultNodePrompts ?? {},
        archetypeNodeModelPolicy: this.selectedArchetype()?.defaultNodeModelPolicy ?? {},
        nodeModelPolicy: this.nodeModelPolicyPayload(),
        modelSource: 'archetype_node_policy',
        builderGenerationModelNode: BUILDER_GENERATION_MODEL_NODE,
        allowedChannelTypes: this.selectedChannelTypes(),
        allowedChannels: this.selectedAllowedChannels().map((channel) => ({
          type: channel.type,
          label: channel.label,
          integrationName: channel.integrationName,
        })),
        presentationName,
        responseLength: this.responseLength(),
        collectionFields: this.collectionFields(),
      },
    };
  }

  private nodeModelPolicyPayload(): Record<string, unknown> {
    return asRecord(this.selectedArchetype()?.defaultNodeModelPolicy);
  }

  private nodeModelPolicyFor(node: string): Record<string, unknown> {
    return asRecord(this.nodeModelPolicyPayload()[node]);
  }

  private firstArchetypeModel(): string {
    for (const policy of Object.values(this.nodeModelPolicyPayload())) {
      const model = readString(asRecord(policy), 'model');
      if (model) return model;
    }
    return FALLBACK_ARCHETYPE_MODEL;
  }

  private ensurePresentationName(): string {
    const current = this.presentationName().trim();
    if (current) return current;

    const name = this.suggestPresentationName();
    this.presentationName.set(name);
    return name;
  }

  private suggestPresentationName(): string {
    return faker.person.firstName();
  }

  private buildVersionDraftRequest(): AgentVersionDraftRequest {
    const patch = this.versionDraftPatch();
    const patchModelConfig = asRecord(patch['modelConfig']);
    const patchBuilder = asRecord(patchModelConfig['builder']);
    const document = this.missionDocument();
    const compiledPrompt =
      this.compiledPrompt().trim() ||
      readString(patch, 'systemPrompt') ||
      this.compileMissionPrompt();
    return {
      // Lets the backend inherit bindings/policies from the version we loaded when it has to
      // create a new version row instead of patching this one in place (e.g. the published
      // version can no longer be patched). Without this, an untouched picker below would be sent
      // as an explicit empty list and silently wipe whatever was already bound to the agent.
      sourceVersionId: this.sourceVersionId(),
      sourceArchetypeVersionId: this.selectedArchetypePublishedVersionId(),
      graphKey: this.selectedGraph()?.key ?? this.api.graphKey(this.graphs()),
      graphVersion: this.selectedGraph()?.version ?? 1,
      modelProvider: 'openrouter',
      model: this.nodeModelValue(RUNTIME_MODEL_NODE),
      systemPrompt: compiledPrompt,
      modelConfig: {
        ...patchModelConfig,
        builder: {
          ...patchBuilder,
          source: 'agent_builder_v0',
          mode: this.mode(),
          missionDocument: document,
          missionSourceDsl: this.missionSourceDsl(),
          tone: this.tone(),
          intensity: this.intensity(),
          intent: this.intent().trim(),
          nodePrompts: this.selectedArchetype()?.defaultNodePrompts ?? asRecord(patchBuilder['nodePrompts']),
          nodeModelPolicy: this.nodeModelPolicyPayload(),
          modelSource: 'archetype_node_policy',
          runtimeModelNode: RUNTIME_MODEL_NODE,
          hasExecutionHistory: this.hasExecutionHistory(),
        },
        builderProvenance: {
          generatedAt: new Date().toISOString(),
          runId: this.missionRunId(),
          routeMode: this.mode(),
          selectedArchetypeId: this.selectedArchetypeId(),
          selectedGraphKey: this.selectedGraph()?.key ?? null,
          selectedGraphVersion: this.selectedGraph()?.version ?? null,
          archetypeNodePrompts: this.selectedArchetype()?.defaultNodePrompts ?? {},
          archetypeNodeModelPolicy: this.selectedArchetype()?.defaultNodeModelPolicy ?? {},
          nodeModelPolicy: this.nodeModelPolicyPayload(),
          selectedIntegrationIds: this.selectedIntegrationIds(),
        },
        agentBindings: {
          subAccountBindings: this.subAccountBindings(),
          agendaVisibility: this.agendaVisibility(),
          eventTemplate: {
            ...(this.eventTemplate().titleDefault.trim()
              ? { titleDefault: this.eventTemplate().titleDefault.trim() }
              : {}),
            ...(this.eventTemplate().durationMinutes
              ? { durationMinutes: this.eventTemplate().durationMinutes }
              : {}),
          },
        },
      },
      retrievalPolicy: {
        strategy: 'agent_selects_relevant_knowledge_bases',
        maxKnowledgeBasesPerTurn: Math.max(
          1,
          Math.min(3, this.selectedKnowledgeBaseIds().length || 1),
        ),
        builderHints: this.retrievalHints(),
      },
      memoryPolicy: {
        scope: 'contact',
        writeCollectedFieldsToContactMemory: true,
        collectionFields: this.collectionFields(),
      },
      // Only send these when the user actually touched the picker in this session. Omitting an
      // untouched list lets the backend fall back to sourceVersionId's existing bindings instead
      // of overwriting them with an empty array derived from a signal that was never hydrated
      // (or not hydrated yet) in this session.
      toolAllowlist: this.toolsTouched() ? this.buildToolAllowlist() : undefined,
      skillAllowlist: this.skillsTouched() ? this.buildSkillAllowlist() : undefined,
      knowledgeBases: this.knowledgeBasesTouched()
        ? this.selectedKnowledgeBaseIds().map((knowledgeBaseId, index) => ({
            knowledgeBaseId,
            retrievalPriority: index + 1,
            filterPolicy: {},
          }))
        : undefined,
    } as AgentVersionDraftRequest;
  }

  private buildToolAllowlist(): AgentVersionToolInput[] {
    return this.selectedTools().map((tool) => ({
      toolDefinitionId: tool.id,
      mode: 'auto',
      argumentPolicy: {
        ...this.serializedToolPolicy(tool),
        requiredPermission: tool.requiredPermission,
        riskLevel: tool.riskLevel,
      },
      resultPolicy: {
        persistRawProviderPayload: false,
        redactCredentials: true,
        summarizeForMemory: tool.riskLevel === 'read' || tool.riskLevel === 'low',
      },
    }));
  }

  private buildSkillAllowlist(): AgentVersionSkillInput[] {
    return this.selectedSkills().flatMap((skill) => {
      const version = this.skillVersions()[skill.id];
      if (!version) return [];
      return [
        {
          agentSkillId: skill.id,
          agentSkillVersionId: version.id,
          skillRevision: version.revision,
          contractHashSnapshot: version.contractHash,
          mode: 'auto',
          skillPolicy: {
            source: 'agent_builder',
            dependencyValidationStatus: version.dependencyValidationStatus,
          },
        },
      ];
    });
  }

  private buildChannelConfigs(): AgentChannelConfigInput[] {
    return this.selectedChannelTypes().map((channelType) => {
      const accounts = this.selectedChannelAccounts().filter(
        (account) => this.toAgentChannelType(account.kind) === channelType,
      );
      return {
        channelType,
        enabled: true,
        behaviorPolicy: {
          ...this.channelBehaviorPolicy(channelType),
          accountIds: accounts.map((account) => account.id),
          mode: this.deploymentMode(),
          memoryScope: 'contact',
        },
      };
    });
  }

  private buildGoals(agentId: string): AgentGoalInput[] {
    return [
      {
        goalType: 'resolution',
        name: 'Cumprir a missão configurada',
        description: this.intent().trim(),
        successPolicy: {
          mode: 'semantic',
          criteria: this.intent().trim(),
          collectionFields: this.collectionFields(),
        },
        metricPolicy: {
          eventKey: 'conversation.message.inbound',
          metric: 'agent_builder_mission_success',
          deterministic: false,
          agentId,
        },
        status: 'active',
      },
    ];
  }

  private persistAcceptedTriggers(agentId: string): Observable<unknown> {
    const accepted = this.triggerDrafts().filter((draft) => draft.accepted);
    if (accepted.length === 0) {
      return this.api.replaceTriggerPolicyAssignments(agentId, []);
    }
    return forkJoin(
      accepted.map((draft) => {
        const input = this.buildTriggerPolicyInput(draft);
        return draft.triggerPolicyId
          ? this.api.updateTriggerPolicy(draft.triggerPolicyId, input)
          : this.api.createTriggerPolicy(input);
      }),
    ).pipe(
      switchMap((policies) =>
        this.api.replaceTriggerPolicyAssignments(
          agentId,
          policies.map((policy, index) => ({
            triggerPolicyId: policy.id,
            sequence: index + 1,
            status: 'active',
            overridePolicy: {
              configuredFrom: 'agent_builder',
              builderDraftId: accepted[index]!.id,
            },
          })),
        ),
      ),
    );
  }

  private persistDeployments(agentId: string): Observable<unknown> {
    const accounts = this.selectedChannelAccounts();
    const selectedAccountIds = new Set(accounts.map((account) => account.id));
    return this.api.listDeployments(agentId).pipe(
      switchMap((response) => {
        const deploymentsByChannelId = new Map<string, AgentDeployment>();
        for (const deployment of response.data) {
          const channelAccountId = this.deploymentChannelAccountId(deployment);
          if (channelAccountId) deploymentsByChannelId.set(channelAccountId, deployment);
        }

        const calls: Observable<unknown>[] = [];
        for (const account of accounts) {
          const request = this.buildDeploymentRequest(agentId, account);
          const existing = deploymentsByChannelId.get(account.id);
          calls.push(
            existing
              ? this.api.updateDeployment(existing.id, request)
              : this.api.createDeployment(request),
          );
        }

        for (const deployment of response.data) {
          const channelAccountId = this.deploymentChannelAccountId(deployment);
          if (
            channelAccountId &&
            !selectedAccountIds.has(channelAccountId) &&
            deployment.status !== 'disabled'
          ) {
            calls.push(this.api.updateDeployment(deployment.id, { status: 'disabled' }));
          }
        }

        return calls.length ? forkJoin(calls) : of([]);
      }),
    );
  }

  private deploymentChannelAccountId(deployment: AgentDeployment): string | null {
    return (
      deployment.contextRefs?.find(
        (ref) => ref.domain === 'integrations' && ref.entityType === 'channel_account',
      )?.id ?? null
    );
  }

  private deploymentSegmentRefId(deployment: AgentDeployment): string | null {
    return (
      deployment.contextRefs?.find((ref) => ref.domain === 'crm' && ref.entityType === 'segment')
        ?.id ?? null
    );
  }

  private buildDeploymentRequest(agentId: string, account: ChannelAccount): AgentDeploymentRequest {
    const channelType = this.toAgentChannelType(account.kind);
    const crmSegmentId = this.deploymentSegmentByAccountId()[account.id] ?? null;
    const versionResolution = this.deploymentVersionResolutionForAccount(account.id);
    const agentVersionId =
      versionResolution === 'fixed_agent_version'
        ? this.deploymentAgentVersionIdByAccountId()[account.id] ?? this.currentVersion()?.id ?? null
        : null;
    const crmSegment = crmSegmentId
      ? this.contactSegments().find((segment) => segment.id === crmSegmentId) ?? null
      : null;
    const contextRefs: BusinessScopedSoftRef[] = [
      {
        domain: 'integrations',
        entityType: 'channel_account',
        id: account.id,
        display: account.label,
      },
    ];
    if (crmSegment) {
      contextRefs.push({
        domain: 'crm',
        entityType: 'segment',
        id: crmSegment.id,
        display: crmSegment.name,
      });
    }
    return {
      agentId,
      versionResolution,
      agentVersionId,
      bindingType: 'channel_account',
      configuredFromDomain: 'integrations',
      status: this.deploymentStatusForAccount(account.id),
      channelAccountId: account.id,
      channelType,
      contextRefs,
      conversationExecutionPolicy: {
        mode: this.deploymentModeForAccount(account.id),
        memoryScope: 'contact',
        groupedInboundMessages: true,
      },
      triggerPolicy: {
        draftTriggerKeys: this.triggerDrafts()
          .filter((draft) => draft.accepted)
          .map((draft) => draft.id),
      },
      initialMessagePolicy: { enabled: false },
      templatePolicy: {
        channelKind: account.kind,
        channelType,
        respectProviderTemplates: true,
      },
    };
  }

  private buildTriggerPolicyInput(draft: BuilderTriggerDraft): AgentTriggerPolicyInput {
    return {
      name: draft.name.trim(),
      description: `Criada pelo Agent Builder para ${draft.eventKey.trim()}.`,
      status: 'active',
      scopePolicy: {
        eventKey: draft.eventKey.trim(),
        source: 'agent_builder',
        channelAccountIds: this.selectedChannelAccountIds(),
        builderDraftId: draft.id,
      },
      evaluationPolicy: {
        evaluationPoint: 'before_agent_response',
        mode: draft.mode,
      },
      rules: [
        {
          sequence: 1,
          name: draft.name.trim(),
          conditionType: draft.mode === 'semantic' ? 'ai_classifier' : 'declarative',
          conditionPolicy: {
            eventKey: draft.eventKey.trim(),
            mode: draft.mode,
            condition: draft.condition.trim(),
          },
          actionPolicy: {
            actions: [{ sequence: 1, action: draft.action }],
          },
          rateLimitPolicy: {
            perContactWindow: 'conversation',
          },
          status: 'active',
        },
      ],
    };
  }

  private prepareMissionGenerationState(): void {
    this.stopMissionRunTracking();
    this.missionRunStatus.set('queued');
    this.missionRunId.set(null);
    this.missionSourceDsl.set('');
    this.missionDiagnostics.set([]);
    this.compiledPrompt.set('');
    this.versionDraftPatch.set({});
    this.streamErrors.set([]);
    this.streamWarnings.set([]);
    this.triggerSuggestions.set([]);
    const document = this.missionDocument();
    if (!document) return;
    this.missionDocument.set({
      ...document,
      diagnostics: [],
      blocks: document.blocks.map((block) => ({ ...block, diagnostics: [] })),
    });
  }

  private stopMissionRunTracking(runId: string | null = this.missionRunId()): void {
    this.missionRunPollSubscription?.unsubscribe();
    this.missionRunPollSubscription = null;
    this.missionRunEventsSubscription?.unsubscribe();
    this.missionRunEventsSubscription = null;
    if (runId) this.realtime.unsubscribe(runId);
  }

  private startMissionRunPolling(runId: string, agentId: string): void {
    this.missionRunPollSubscription?.unsubscribe();
    let attempts = 0;
    this.missionRunPollSubscription = timer(2500, 2500)
      .pipe(
        switchMap(() => {
          attempts += 1;
          return this.api
            .listRuns({ agentId, pageSize: 20 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (this.missionRunId() !== runId || !this.isMissionRunPending()) {
          this.stopMissionRunTracking(runId);
          return;
        }
        const run = response?.data.find((item) => item.id === runId) ?? null;
        if (run) {
          this.applyMissionRunSnapshot(run);
          return;
        }
        if (attempts >= this.missionRunPollMaxAttempts) {
          this.failMissionRunFromTimeout(runId);
        }
      });
  }

  private isMissionRunPending(): boolean {
    const status = this.missionRunStatus();
    return status === 'queued' || status === 'running';
  }

  private applyMissionRunSnapshot(run: AgentRun): void {
    if (run.id !== this.missionRunId()) return;
    if (run.status === 'queued' || run.status === 'running') {
      this.missionRunStatus.set(run.status);
      return;
    }

    const status = run.status === 'succeeded' ? 'completed' : 'failed';
    this.applyMissionGenerationPayload(runOutputPayload(run));
    this.missionRunStatus.set(status);
    this.finishMissionRun(run.id, status);

    if (status === 'failed' && this.streamErrors().length === 0) {
      this.streamErrors.set([
        run.status === 'timed_out'
          ? 'A geração da missão excedeu o tempo limite.'
          : 'A missão foi gerada com problemas. Revise os diagnósticos antes de publicar.',
      ]);
    }
  }

  private applyMissionGenerationPayload(payload: unknown): void {
    const output = asRecord(payload);
    const validation = asRecord(output['validation']);
    const missionSourceDsl = readString(output, 'missionSourceDsl');
    if (missionSourceDsl) this.missionSourceDsl.set(missionSourceDsl);

    let nextDocument: AgentMissionDocument | null = null;
    const document = validation['document'] ?? output['missionDocument'];
    if (isMissionDocument(document)) {
      nextDocument = sanitizeMissionDocument(document);
      this.missionDocument.set(nextDocument);
      this.missionDiagnostics.set(nextDocument.diagnostics);
      this.syncCollectionFieldsFromMission(nextDocument);
    }

    const diagnostics = validation['diagnostics'];
    if (Array.isArray(diagnostics)) {
      this.missionDiagnostics.set(
        sanitizeMissionDiagnostics(diagnostics as AgentMissionDiagnostic[], nextDocument),
      );
    }

    const validationPatch = asRecord(validation['versionDraftPatch']);
    const outputPatch = asRecord(output['versionDraftPatch']);
    const versionDraftPatch = Object.keys(validationPatch).length
      ? validationPatch
      : outputPatch;
    if (Object.keys(versionDraftPatch).length) this.versionDraftPatch.set(versionDraftPatch);

    const compiledPrompt =
      readString(validation, 'compiledPrompt') || readString(output, 'compiledPrompt');
    if (compiledPrompt) {
      this.compiledPrompt.set(compiledPrompt);
    } else if (this.missionDocument()) {
      this.compiledPrompt.set(this.compileMissionPrompt());
    }
  }

  private finishMissionRun(runId: string, status: MissionRunStatus): void {
    this.stopMissionRunTracking(runId);
    if (status === 'completed') {
      this.suggestTriggers();
      this.saveIncremental(true)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => of(null)),
        )
        .subscribe();
    }
  }

  private failMissionRunFromTimeout(runId: string): void {
    if (this.missionRunId() !== runId || !this.isMissionRunPending()) return;
    this.missionRunStatus.set('failed');
    this.streamErrors.set([
      'A geração da missão demorou mais que o esperado. Tente gerar novamente.',
    ]);
    this.stopMissionRunTracking(runId);
  }

  applyRunEvent(event: AgentBuilderRunEvent): void {
    if (event.runId !== this.missionRunId()) return;
    if (event.status === 'running') this.missionRunStatus.set('running');
    if (event.type === 'mission_source_dsl_chunk') {
      const chunk = readString(event.payload, 'chunk');
      const complete = event.payload['complete'] === true;
      if (chunk) {
        if (complete) {
          this.missionSourceDsl.set(chunk);
        } else {
          this.missionSourceDsl.update((current) => current + chunk);
        }
      }
    }
    if (event.type === 'mission_document_patch') {
      const document = event.payload['document'];
      if (isMissionDocument(document)) {
        const sanitizedDocument = sanitizeMissionDocument(document);
        this.missionDocument.set(sanitizedDocument);
        this.missionDiagnostics.set(sanitizedDocument.diagnostics);
        this.syncCollectionFieldsFromMission(sanitizedDocument);
      }
    }
    if (event.type === 'version_draft_patch_chunk') {
      this.versionDraftPatch.set(asRecord(event.payload['patch']));
    }
    if (event.type === 'warning') {
      this.streamWarnings.update((warnings) => [
        ...warnings,
        readString(event.payload, 'message') || 'Aviso na geração da missão.',
      ]);
    }
    if (event.type === 'recoverable_error') {
      this.streamErrors.update((errors) => [
        ...errors,
        readString(event.payload, 'message') || 'Erro recuperável na geração.',
      ]);
    }
    if (event.type === 'completed' || event.type === 'failed') {
      const status = event.type === 'completed' ? 'completed' : 'failed';
      this.applyMissionGenerationPayload(event.payload);
      this.missionRunStatus.set(status);
      this.finishMissionRun(event.runId, status);
      const message = readString(event.payload, 'message');
      if (status === 'failed' && message) this.streamErrors.update((errors) => [...errors, message]);
    }
  }

  private suggestTriggers(): void {
    this.api
      .suggestBuilderTriggers({
        objective: this.intent().trim(),
        selectedIntegrations: this.selectedIntegrations().map((integration) => ({
          id: integration.id,
          provider: integration.provider,
          displayName: integration.displayName,
        })),
        channelAccounts: this.selectedChannelAccounts().map((account) => ({
          id: account.id,
          kind: account.kind,
          label: account.label,
        })),
        missionDocument: this.missionDocument() ?? undefined,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        if (!response) return;
        this.triggerSuggestions.set(response.data);
      });
  }

  private referenceCatalog(): AgentBuilderReferenceCatalog {
    return {
      knowledgeBaseIds: this.knowledgeBases().map((base) => base.id),
      toolDefinitionIds: this.tools().map((tool) => tool.id),
      skillVersionIds: Object.values(this.skillVersions()).map((version) => version.id),
      integrationIds: this.integrations().map((integration) => integration.id),
      channelAccountIds: this.channelAccounts().map((account) => account.id),
      automationIds: [
        ...SYSTEM_AUTOMATION_MENTIONS.map((automation) => automation.targetId),
        ...this.automations().map((automation) => automation.id),
      ],
      triggerEventKeys: [
        'conversation.message.inbound',
        'conversation.started',
        'conversation.assigned',
        'crm.contact.created',
        'crm.contact.updated',
        'crm.deal.created',
        'crm.deal.updated',
        'integrations.provider_object.created',
        'integrations.provider_object.updated',
        'revenue.checkout.created',
      ],
    };
  }

  private withParsedMissionReferences(block: AgentMissionBlock): AgentMissionBlock {
    return {
      ...block,
      references: this.extractMissionReferences(block.body),
    };
  }

  private extractMissionReferences(value: string): AgentMissionReference[] {
    const references: AgentMissionReference[] = [];
    const pattern =
      /(!tool|@library|@skill|>automation|#automation|\$field|!trigger|#stage|\?handoff):([a-zA-Z0-9._:-]+)/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(value)) !== null) {
      const targetId = normalizeMissionReferenceTarget(match[2]!);
      const raw = `${match[1]}:${targetId}`;
      const mention = this.missionMentionOptions().find((option) => option.raw === raw);
      const fallbackType = missionMentionTypeFromPrefix(match[1]);
      const reference: AgentMissionReference = mention
        ? {
            type: mention.type,
            raw: mention.raw,
            targetId: mention.targetId,
            label: mention.label,
            metadata: { source: 'agent_builder' },
          }
        : {
            type: fallbackType,
            raw,
            targetId,
            metadata: { source: 'typed_reference' },
          };
      const next = upsertReference(references, reference);
      references.splice(0, references.length, ...next);
    }
    return references;
  }

  private collectMissionReferences(
    blocks: readonly AgentMissionBlock[],
    fallback: readonly AgentMissionReference[] = [],
  ): AgentMissionReference[] {
    return blocks
      .flatMap((block) => block.references)
      .reduce<
        AgentMissionReference[]
      >((references, reference) => upsertReference(references, reference), [...fallback.filter((reference) => blocks.some((block) => block.body.includes(reference.raw)))]);
  }

  private toolRiskCopy(risk: string): string {
    return (
      {
        read: 'Consulta dados sem alterar registros.',
        low: 'Baixo impacto operacional.',
        medium: 'Pode alterar dados; use com critério.',
        high: 'Ação sensível; descreva quando executar.',
        irreversible: 'Ação irreversível; exige regra clara.',
      }[risk] ?? 'Capacidade operacional'
    );
  }

  private retrievalHints(): Record<string, unknown> {
    return {
      byKnowledgeType: this.selectedKnowledgeBases().map((base) => ({
        knowledgeBaseId: base.id,
        type: base.type,
        hint: retrievalHintForType(base.type),
      })),
    };
  }

  private compileMissionPrompt(): string {
    const document = this.missionDocument();
    const title = document?.title || this.agentName().trim() || 'Missão do agente';
    const blocks = document?.blocks ?? [];
    return [
      `# ${title}`,
      this.intent().trim() ? `Objetivo: ${this.intent().trim()}` : '',
      `Tom: ${this.toneOption().policy}. Intensidade: ${this.intensityOption().policy}.`,
      ...blocks.map((block, index) => `## ${index + 1}. ${block.title}\n${block.body}`),
      this.selectedToolMentions().length
        ? `Tools habilitadas:\n${this.selectedToolMentions()
            .map((tool) => `- !tool:${tool.id}: ${tool.label}. Risco: ${tool.riskLevel}.`)
            .join('\n')}`
        : '',
      'Use somente bibliotecas, tools, skills e canais habilitados nesta versão. Faça handoff quando faltar contexto, permissão ou fonte de verdade.',
    ]
      .filter(Boolean)
      .join('\n\n');
  }

  private emptyMissionDocument(): AgentMissionDocument {
    const intent = this.intent().trim();
    return {
      schema: 'agent-mission-v1',
      version: 1,
      locale: 'pt-BR',
      title: this.agentName().trim() || 'Missão do agente',
      summary: intent || 'Missao criada no Agent Builder.',
      blocks: [
        {
          id: 'mission-stage-1',
          type: 'stage',
          slug: 'atendimento_inicial',
          title: 'Atendimento inicial',
          body:
            intent ||
            'Entenda a intencao do cliente, colete o minimo necessario, consulte fontes permitidas quando houver duvida operacional e acione handoff quando faltar contexto, permissao ou fonte confiavel.',
          fields: {},
          references: [],
          diagnostics: [],
        },
      ],
      references: [],
      diagnostics: [],
    };
  }

  private legacyPromptMissionDocument(systemPrompt: string): AgentMissionDocument {
    const body = systemPrompt.trim();
    return {
      schema: 'agent-mission-v1',
      version: 1,
      locale: 'pt-BR',
      title: this.agentName().trim() || 'Missao do agente',
      summary: this.intent().trim() || 'Prompt legado importado para edicao.',
      blocks: [
        {
          id: 'legacy-system-prompt',
          type: 'stage',
          slug: 'prompt-legado',
          title: 'Instrucoes legadas',
          body,
          fields: {
            importedFrom: 'systemPrompt',
          },
          references: [],
          diagnostics: [],
        },
      ],
      references: [],
      diagnostics: [],
    };
  }

  private channelBehaviorPolicy(
    channelType: AgentChannelConfigChannelTypeEnumKey,
  ): Record<string, unknown> {
    const behavior = this.channelBehaviors()[channelType];
    return {
      toneStylePolicy: { style: behavior.toneStyle.trim() },
      replyMode: this.deploymentMode(),
      messageLengthPolicy: { level: behavior.messageLength },
      templatePolicy: { rule: behavior.templatePolicy.trim() },
      mediaPolicy: { rule: behavior.mediaPolicy.trim() },
      asyncWindowPolicy: { rule: behavior.asyncWindowPolicy.trim() },
      fallbackPolicy: { rule: behavior.fallbackPolicy.trim() },
    };
  }

  private defaultChannelBehavior(
    channelType: AgentChannelConfigChannelTypeEnumKey,
  ): ChannelBehaviorDraft {
    return {
      channelType,
      enabled: true,
      replyMode: 'automatic_reply',
      toneStyle: 'Claro, profissional e direto, com linguagem natural em pt-BR.',
      messageLength: channelType === 'email' ? 'detalhada' : 'normal',
      templatePolicy:
        'Usar templates aprovados quando o provider exigir ou quando a conversa estiver fora da janela livre.',
      mediaPolicy: 'Texto por padrão; mídia somente quando o canal e a conta permitirem.',
      asyncWindowPolicy:
        'Respeitar janela do provider e pausar follow-up quando a conta estiver indisponível.',
      fallbackPolicy:
        'Transferir para humano quando faltar contexto, permissão, Knowledge ou capacidade do canal.',
    };
  }

  private toAgentChannelType(kind: string): AgentChannelConfigChannelTypeEnumKey {
    if (kind === 'instagram') return 'instagram_dm';
    if (kind === 'whatsapp' || kind === 'email' || kind === 'sms' || kind === 'webchat')
      return kind;
    return 'custom';
  }

  private coerceChannelTypes(values: readonly string[]): AgentChannelConfigChannelTypeEnumKey[] {
    return this.sortChannelTypes(
      values.filter((value): value is AgentChannelConfigChannelTypeEnumKey =>
        this.isAgentChannelType(value),
      ),
    );
  }

  private channelTypesFromAccountIds(
    accountIds: readonly string[],
  ): AgentChannelConfigChannelTypeEnumKey[] {
    const ids = new Set(accountIds);
    return this.sortChannelTypes(
      this.channelAccounts()
        .filter((account) => ids.has(account.id))
        .map((account) => this.toAgentChannelType(account.kind)),
    );
  }

  private isAgentChannelType(value: string): value is AgentChannelConfigChannelTypeEnumKey {
    return this.allowedChannelOptions.some((option) => option.type === value);
  }

  private sortChannelTypes(
    types: readonly AgentChannelConfigChannelTypeEnumKey[],
  ): AgentChannelConfigChannelTypeEnumKey[] {
    const order = new Map(
      this.allowedChannelOptions.map((channel, index) => [channel.type, index]),
    );
    return [...new Set(types)].sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999));
  }

  private toneOption() {
    return TONE_OPTIONS.find((option) => option.value === this.tone()) ?? TONE_OPTIONS[0]!;
  }

  private intensityOption() {
    return (
      INTENSITY_OPTIONS.find((option) => option.value === this.intensity()) ?? INTENSITY_OPTIONS[0]!
    );
  }

  private missionRunStatusLabel(): string {
    const status = this.missionRunStatus();
    if (status === 'queued') return 'Na fila';
    if (status === 'running') return 'Gerando';
    if (status === 'completed') return 'Gerada';
    if (status === 'failed') return 'Falhou';
    return 'Aguardando geração';
  }

  private slimArchetype(archetype: AgentArchetype): Record<string, unknown> {
    return {
      id: archetype.id,
      key: archetype.key,
      name: archetype.name,
      description: archetype.description,
      defaultGraphKey: archetype.defaultGraphKey,
      defaultGraphVersion: archetype.defaultGraphVersion,
      defaultNodePrompts: archetype.defaultNodePrompts,
      defaultNodeModelPolicy: archetype.defaultNodeModelPolicy,
      defaultGoalPolicy: archetype.defaultGoalPolicy,
      defaultPersona: archetype.defaultPersona,
      currentPublishedVersionId: readString(archetype as unknown as Record<string, unknown>, 'currentPublishedVersionId') || null,
    };
  }

  private selectedArchetypePublishedVersionId(): string | null {
    const archetype = this.selectedArchetype();
    return archetype
      ? readString(archetype as unknown as Record<string, unknown>, 'currentPublishedVersionId') || null
      : null;
  }

  private toggleId(target: WritableSignal<string[]>, id: string): void {
    target.update((ids) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]));
    this.markDirty();
  }

  private uniqueCollectionFieldKey(label: string): string {
    const base = fieldKeyFromLabel(label);
    const existingKeys = new Set(this.collectionFields().map((field) => field.key));
    if (!existingKeys.has(base)) return base;
    for (let suffix = 2; suffix < 1000; suffix += 1) {
      const candidate = `${base}_${suffix}`;
      if (!existingKeys.has(candidate)) return candidate;
    }
    return `${base}_${Date.now().toString(36).slice(-4)}`;
  }

  private updateToolFields(
    toolId: string,
    updater: (fields: BuilderToolFieldView[]) => BuilderToolFieldView[],
  ): void {
    const tool = this.tools().find((item) => item.id === toolId);
    if (!tool) return;
    const current = this.toolFieldViews(tool);
    const next = updater(current);
    this.toolPolicies.update((policies) => ({
      ...policies,
      [toolId]: compactToolPolicyDraft(tool, next),
    }));
    this.markDirty();
  }

  private serializedToolPolicy(tool: AgentToolDefinition): BuilderToolPolicyDraft {
    return compactToolPolicyDraft(tool, this.toolFieldViews(tool));
  }

  private toolFieldCatalogs(): ToolFieldCatalogs {
    return {
      channelAccounts: this.channelAccounts(),
      selectedChannelAccountIds: this.selectedChannelAccountIds(),
      integrations: this.integrations(),
      selectedIntegrationIds: this.selectedIntegrationIds(),
      knowledgeBases: this.knowledgeBases(),
      businessHoursCalendars: this.businessHoursCalendars(),
      agentSubAccountBindings: this.subAccountBindings(),
    };
  }

  /** Distinct agent-level bindings derived from the enabled tools' markers. */
  readonly agentBindingDescriptors = computed(() => bindDescriptorsFromTools(this.selectedTools()));

  readonly hasAgentBindings = computed(() => this.agentBindingDescriptors().length > 0);

  /** Options for a descriptor's contextual selector (e.g. connected agendas). */
  bindDescriptorOptions(descriptor: BindDescriptor): readonly BuilderToolFieldOption[] {
    return optionsForBindDescriptor(descriptor, this.toolFieldCatalogs());
  }

  agentBindingValue(descriptorKey: string): string {
    return this.subAccountBindings()[descriptorKey] ?? '';
  }

  /** Whether to show the event-template config (a calendar create/update tool is enabled). */
  readonly hasEventTemplateConfig = computed(() =>
    this.selectedTools().some((tool) => tool.key === 'calendar.events.create' || tool.key === 'calendar.events.update'),
  );

  /** Rótulo legível da visibilidade da agenda selecionada. */
  readonly agendaVisibilityLabel = computed(() =>
    this.agendaVisibility() === 'internal' ? 'Interno — vê todos os eventos' : 'Cliente — vê só os próprios',
  );

  setAgendaVisibility(value: string): void {
    this.agendaVisibility.set(value === 'internal' ? 'internal' : 'client');
    this.versionDirty.set(true);
  }

  eventTitleDefault(): string {
    return this.eventTemplate().titleDefault;
  }

  eventDurationMinutes(): number | null {
    return this.eventTemplate().durationMinutes;
  }

  setEventTitleDefault(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.eventTemplate.update((template) => ({ ...template, titleDefault: value }));
    this.versionDirty.set(true);
  }

  setEventDurationMinutes(event: Event): void {
    const parsed = Number((event.target as HTMLInputElement).value);
    this.eventTemplate.update((template) => ({
      ...template,
      durationMinutes: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    }));
    this.versionDirty.set(true);
  }

  /** Label of the currently pinned option for a descriptor (for the select trigger). */
  bindDescriptorSelectedLabel(descriptor: BindDescriptor): string {
    const value = this.agentBindingValue(descriptor.key);
    if (!value) return '';
    return this.bindDescriptorOptions(descriptor).find((option) => option.value === value)?.label ?? value;
  }

  /** Pins (or clears) an agent-level binding for a descriptor. */
  setAgentBinding(descriptorKey: string, value: string | null): void {
    this.subAccountBindings.update((current) => {
      const next = { ...current };
      if (value) next[descriptorKey] = value;
      else delete next[descriptorKey];
      return next;
    });
    this.versionDirty.set(true);
  }
}

function emptyFieldValue(kind: BuilderToolFieldKind): BuilderToolFieldValue {
  if (kind === 'multi_select') return [];
  if (kind === 'boolean') return false;
  return null;
}

function upsertReference(
  references: readonly AgentMissionReference[],
  reference: AgentMissionReference,
): AgentMissionReference[] {
  if (
    references.some((item) => item.type === reference.type && item.targetId === reference.targetId)
  ) {
    return [...references];
  }
  return [...references, reference];
}

function defaultCollectionFields(): BuilderCollectionField[] {
  return [
    {
      id: nextLocalId('field'),
      label: 'Nome do contato',
      key: 'contact.name',
      source: 'contact',
      required: true,
      writeToMemory: true,
      description: 'Nome preferido para personalizar o atendimento.',
    },
    {
      id: nextLocalId('field'),
      label: 'Motivo do atendimento',
      key: 'conversation.intent',
      source: 'conversation',
      required: true,
      writeToMemory: true,
      description: 'Resumo curto do problema, dúvida ou pedido.',
    },
  ];
}

function coerceCollectionFields(value: unknown): BuilderCollectionField[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const record = asRecord(item);
    const label = readString(record, 'label').trim();
    if (!label) return [];
    const source = collectionFieldSource(readString(record, 'source'));
    return [
      {
        id: readString(record, 'id') || nextLocalId('field'),
        label,
        key: readString(record, 'key') || fieldKeyFromLabel(label, source),
        source,
        required: readBoolean(record, 'required', true),
        writeToMemory: readBoolean(record, 'writeToMemory', true),
        description:
          readString(record, 'description') || 'Campo coletado pelo agente durante o atendimento.',
      },
    ];
  });
}

function fieldKeyFromLabel(
  label: string,
  source: BuilderCollectionField['source'] = 'conversation',
): string {
  const slug =
    normalizeSearchText(label)
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || nextLocalId('field');
  return source === 'conversation' ? `${FIELD_SOURCE_PREFIX}.${slug}` : `${source}.${slug}`;
}

function missionFieldLabel(reference: AgentMissionReference): string {
  const label = reference.label?.trim() ?? '';
  if (label && label !== reference.raw) return label;
  return '';
}

function collectionFieldLabelFromKey(key: string): string {
  const raw = key.split('.').pop() || key;
  const label = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Campo';
  return label.replace(/^./u, (letter) => letter.toLocaleUpperCase('pt-BR'));
}

function collectionFieldSource(value: string): BuilderCollectionField['source'] {
  if (value === 'contact' || value === 'conversation' || value === 'event') return value;
  return 'conversation';
}

function nextLocalId(prefix: string): string {
  localId += 1;
  return `${prefix}-${localId}`;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(source: unknown, key: string): string {
  const value = asRecord(source)[key];
  return typeof value === 'string' ? value : '';
}

/** Coerces an unknown jsonb value into a string→string map, dropping non-string entries. */
function coerceStringMap(value: unknown): Record<string, string> {
  const record = asRecord(value);
  const out: Record<string, string> = {};
  for (const [key, item] of Object.entries(record)) {
    if (typeof item === 'string' && item.length > 0) out[key] = item;
  }
  return out;
}

function readBoolean(source: unknown, key: string, fallback: boolean): boolean {
  const value = asRecord(source)[key];
  return typeof value === 'boolean' ? value : fallback;
}

function readStringArray(source: unknown, key: string): string[] {
  const value = asRecord(source)[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function isMissionDocument(value: unknown): value is AgentMissionDocument {
  const record = asRecord(value);
  return record['schema'] === 'agent-mission-v1' && Array.isArray(record['blocks']);
}

function sanitizeMissionDocument(document: AgentMissionDocument): AgentMissionDocument {
  return {
    ...document,
    diagnostics: sanitizeMissionDiagnostics(document.diagnostics, document),
    blocks: document.blocks.map((block) => ({
      ...block,
      diagnostics: sanitizeMissionDiagnostics(block.diagnostics, document),
    })),
  };
}

function sanitizeMissionDiagnostics(
  diagnostics: readonly AgentMissionDiagnostic[],
  document: AgentMissionDocument | null,
): AgentMissionDiagnostic[] {
  return diagnostics.filter((diagnostic) => !isStaleParserStructureDiagnostic(diagnostic, document));
}

function isStaleParserStructureDiagnostic(
  diagnostic: AgentMissionDiagnostic,
  document: AgentMissionDocument | null,
): boolean {
  if (diagnostic.code === 'mission_unmatched_end' || diagnostic.code === 'mission_block_not_closed') {
    return true;
  }
  return diagnostic.code === 'mission_missing_blocks' && Boolean(document?.blocks.length);
}

function selectEditableVersion(
  versions: readonly AgentVersion[],
  currentVersionId: string | null,
): AgentVersion | null {
  const draft = versions.find((version) => version.status === 'draft') ?? null;
  const current = currentVersionId
    ? versions.find((version) => version.id === currentVersionId) ?? null
    : null;
  if (draft && !shouldPreferCurrentLegacyVersion(draft, current)) return draft;
  return current ?? draft ?? versions[0] ?? null;
}

function upsertVersion(versions: readonly AgentVersion[], nextVersion: AgentVersion): AgentVersion[] {
  const exists = versions.some((version) => version.id === nextVersion.id);
  const next = exists
    ? versions.map((version) => (version.id === nextVersion.id ? nextVersion : version))
    : [nextVersion, ...versions];
  return next.slice().sort((a, b) => b.version - a.version);
}

function shouldPreferCurrentLegacyVersion(
  draft: AgentVersion,
  current: AgentVersion | null,
): boolean {
  if (!current?.systemPrompt?.trim()) return false;
  const builder = asRecord(asRecord(draft.modelConfig)['builder']);
  const document = builder['missionDocument'];
  return isMissionDocument(document) && document.blocks.length === 0;
}

function runOutputPayload(run: AgentRun): unknown {
  const record = asRecord(run);
  return record['outputSnapshot'] ?? record['output'] ?? {};
}

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function promptTokens(value: string): string[] {
  const stopWords = new Set([
    'com',
    'das',
    'dos',
    'para',
    'por',
    'que',
    'uma',
    'uns',
    'das',
    'meu',
    'minha',
    'nosso',
    'nossa',
    'agent',
    'agente',
    'fazer',
    'criar',
  ]);
  return normalizeSearchText(value)
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2 && !stopWords.has(token));
}

function scorePromptMatch(tokens: readonly string[], value: string): number {
  const normalized = normalizeSearchText(value);
  return tokens.reduce((score, token) => {
    if (!normalized.includes(token)) return score;
    return score + (normalized.split(token).length > 2 ? 2 : 1);
  }, 0);
}

function normalizeAgentName(value: string): string {
  return normalizeSearchText(value).replace(/\s+/g, ' ').trim();
}

function retrievalHintForType(type: string): string {
  const map: Record<string, string> = {
    sales_scripts: 'Priorize exemplos de abordagem, objeções e respostas comerciais aprovadas.',
    processes:
      'Use como guia de sequência operacional, responsabilidades e critérios de conclusão.',
    policies: 'Use como fonte normativa e faça handoff quando a política não cobrir o caso.',
    faq: 'Use para respostas objetivas a dúvidas frequentes e cite limitações quando faltar contexto.',
    product_catalog:
      'Use para validar disponibilidade, características e diferenças entre produtos ou serviços.',
    playbooks: 'Use como roteiro prático de execução, qualificação e próximos passos.',
    other: 'Use como referência auxiliar e prefira fontes mais específicas quando houver conflito.',
    sales_script: 'Priorize exemplos de abordagem, objeções e respostas comerciais aprovadas.',
    process: 'Use como guia de sequência operacional, responsabilidades e critérios de conclusão.',
    policy: 'Use como fonte normativa e faça handoff quando a política não cobrir o caso.',
    playbook: 'Use como roteiro prático de execução, qualificação e próximos passos.',
  };
  return map[type] ?? map['other']!;
}

function slugify(value: string): string {
  return (
    normalizeSearchText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || nextLocalId('block')
  );
}

function missionBlockDefaultTitle(type: AgentMissionBlock['type']): string {
  const labels: Record<AgentMissionBlock['type'], string> = {
    stage: 'Nova etapa',
    trigger_suggestion: 'Gatilho sugerido',
    retrieval: 'Consulta de conhecimento',
    collection: 'Coleta de dados',
    handoff: 'Regra de transferência',
    policy: 'Política de resposta',
    note: 'Nota operacional',
  };
  return labels[type] ?? 'Nova etapa';
}

function missionBlockDefaultBody(type: AgentMissionBlock['type']): string {
  const bodies: Record<AgentMissionBlock['type'], string> = {
    stage:
      'Defina quando esta etapa começa, o que o agente deve resolver e qual é o próximo passo.',
    trigger_suggestion: 'Descreva o evento de entrada e a condição que deve iniciar esta ação.',
    retrieval:
      'Liste quais bibliotecas o agente deve consultar e quando deve responder que não há fonte suficiente.',
    collection: 'Liste os dados que o agente precisa coletar antes de agir ou transferir.',
    handoff:
      'Defina quando o agente deve transferir para uma pessoa e que contexto deve enviar junto.',
    policy: 'Defina uma regra de comportamento, segurança, tom ou uso de canal.',
    note: 'Registre uma observação importante para a operação do agente.',
  };
  return bodies[type] ?? bodies.note;
}

function missionMentionTypeFromPrefix(prefix: string): AgentMissionReference['type'] {
  if (prefix === '!tool') return 'tool';
  if (prefix === '@library') return 'library';
  if (prefix === '@skill') return 'skill';
  if (prefix === '>automation' || prefix === '#automation') return 'automation';
  if (prefix === '!trigger') return 'trigger';
  if (prefix === '#stage') return 'stage';
  if (prefix === '?handoff') return 'handoff';
  return 'field';
}

function normalizeMissionReferenceTarget(value: string): string {
  return value.replace(/[.,;!?]+$/g, '');
}

function createIdempotencyKey(): string {
  return `agent-builder-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isTone(value: unknown): value is AgentBuilderTone {
  return TONE_OPTIONS.some((option) => option.value === value);
}

function isIntensity(value: unknown): value is AgentBuilderIntensity {
  return INTENSITY_OPTIONS.some((option) => option.value === value);
}

function isDeploymentMode(value: unknown): value is AgentBuilderDeploymentMode {
  return DEPLOYMENT_MODE_OPTIONS.some((option) => option.value === value);
}

function isDeploymentStatus(value: unknown): value is AgentBuilderDeploymentStatus {
  return DEPLOYMENT_STATUS_OPTIONS.some((option) => option.value === value);
}

function isVersionResolution(value: unknown): value is AgentBuilderVersionResolution {
  return VERSION_RESOLUTION_OPTIONS.some((option) => option.value === value);
}

function isTriggerAction(value: unknown): value is BuilderTriggerDraft['action'] {
  return (
    value === 'handoff' ||
    value === 'automation' ||
    value === 'system_action' ||
    value === 'retry' ||
    value === 'block' ||
    value === 'route' ||
    value === 'notify' ||
    value === 'escalate'
  );
}

function isTriggerMode(value: unknown): value is BuilderTriggerDraft['mode'] {
  return value === 'semantic' || value === 'deterministic';
}

function isMessageLength(value: unknown): value is ChannelBehaviorDraft['messageLength'] {
  return value === 'curta' || value === 'normal' || value === 'detalhada';
}

function firstToggleValue(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}
